import { useState, useMemo, useEffect } from 'react';
import { isClosingMatterStatus, isTerminalMatterStatus, MATTER_STATUSES } from '@/lib/matterStatus';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useAuthStore } from '@/features/auth/store';
import { MatterAccessPanel } from '@/components/team/MatterAccessPanel';
import { MatterIntelligencePanel } from '@/components/matter-dashboard/MatterIntelligencePanel';
import { useMattersStore, type Matter } from '@/lib/mattersStore';
import { fetchNotes, createNote, updateNote, deleteNote, authorName } from '@/lib/notesApi';
import { MatterCaseRecordPanel } from '@/components/matter-dashboard/MatterCaseRecordPanel';
import { MatterInformationBarriersPanel } from '@/components/matter-dashboard/MatterInformationBarriersPanel';
import { fetchMatter, fetchMatterRoles, fetchMatterFinancialSummary, updateMatter as apiUpdateMatter, archiveMatter, restoreMatter, fetchHearingReadiness } from '@/lib/mattersApi';
import { CloseMatterModal } from '@/components/shared/CloseMatterModal';
import { fetchUsers, type ApiUser } from '@/lib/usersApi';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import { fetchPracticeAreas, type PracticeArea } from '@/lib/practiceAreasApi';
import { fetchTasks, updateTask as updateTaskApi, STATUS_LABELS as TASK_STATUS_LABELS, ACTIVE_STATUSES, type TaskPriority, type TaskStatus, type Task } from '@/lib/tasksApi';
import { fetchDocuments, uploadDocument as uploadDocumentApi, deleteDocument } from '@/lib/documentsApi';
import { fetchFeeRecords, deleteFeeRecord } from '@/lib/feesApi';
import type { TimeEntry } from '@/lib/timeEntriesStore';
import { fetchActivities } from '@/lib/activitiesApi';
import { fetchFeeNotes, STATUS_LABELS as FEE_NOTE_STATUS_LABEL, type FeeNoteStatus } from '@/lib/feeNotesApi';
import { fetchCreditNotes, voidCreditNote, STATUS_LABELS as CREDIT_NOTE_STATUS_LABEL } from '@/lib/creditNotesApi';
import { fetchConferences, PURPOSE_LABELS, FORMAT_LABELS, type Conference } from '@/lib/conferencesApi';
import { fetchCalendarEvents } from '@/lib/calendarApi';
import { KIND_LABEL } from '@/lib/diaryStore';
import { ConferenceWorkspace } from '@/components/conferences/ConferenceWorkspace';

function toTitleCase(str: string): string {
  const minors = new Set(['v', 'vs', 're', 'in', 'of', 'the', 'and', 'for', 'a', 'an']);
  return str
    .split(' ')
    .map((w, i) => (i === 0 || !minors.has(w.toLowerCase()))
      ? w.charAt(0).toUpperCase() + w.slice(1)
      : w.toLowerCase())
    .join(' ');
}

function feeNoteStatusVariant(s: FeeNoteStatus): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'gold' | 'outline' {
  switch (s) {
    case 'draft': return 'default';
    case 'pending_approval': return 'warning';
    case 'approved': return 'info';
    case 'sent': return 'info';
    case 'partially_paid': return 'gold';
    case 'paid': return 'success';
    case 'overdue': return 'danger';
    case 'cancelled': return 'outline';
    case 'written_off': return 'outline';
    case 'partially_credited': return 'gold';
    case 'credited': return 'success';
    default: return 'default';
  }
}
import {
  CaretLeft as ChevronLeft,
  CaretDown,
  PencilSimple as Edit,
  Briefcase,
  CalendarBlank as Calendar,
  Gavel,
  FileText,
  ListChecks,
  Wallet,
  Buildings as Building2,
  Scales as Scale,
  Plus,
  Clock,
  Trophy,
  ChatCircle,
  ChatCircleDots,
  Note as NoteIcon,
  CurrencyGbp,
  CheckCircle,
  WarningCircle,
  Circle,
  ArrowSquareOut,
  ClockClockwise,
  ClockCounterClockwise,
  FilePdf,
  FileDoc,
  MapPin,
  DownloadSimple as Download,
  Trash,
  Eye,
  Star,
  X,
  Printer,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MatterStatusBadge } from '@/components/shared/MatterStatusBadge';
import { AllocationStatusCard } from '@/components/shared/AllocationStatusCard';
import { AllocationSuggestions } from '@/components/shared/AllocationSuggestions';
import { useChambersMode } from '@/lib/useChambersMode';
import { NewFeeRecordModal } from '@/components/shared/NewFeeRecordModal';
import { NewFeeNoteModal } from '@/components/shared/NewFeeNoteModal';
import { FeeNoteActionsButton } from '@/components/fee-notes/FeeNoteActionsButton';
import { RecordPaymentModal } from '@/components/payments/RecordPaymentModal';
import { VoidReasonModal } from '@/components/payments/VoidReasonModal';
import { RowActions } from '@/components/shared/RowActions';
import { fetchPayments, voidPayment } from '@/lib/paymentsApi';
import { TaskModal } from '@/components/tasks/TaskModal';
import { TaskDetailPanel } from '@/components/tasks/TaskDetailPanel';
import { CompleteTaskDialog } from '@/components/tasks/CompleteTaskDialog';
import { AddPartyModal } from '@/components/shared/AddPartyModal';
import { MessagingView } from '@/components/messaging/MessagingView';
import { fetchThreads } from '@/lib/messagingApi';
import { MatterFinancialWidgets } from '@/components/matter-dashboard/MatterFinancialWidgets';
import { MatterMoneySituation } from '@/components/shared/MatterMoneySituation';
import { MatterCourtSituation } from '@/components/shared/MatterCourtSituation';
import { MatterPaymentStory } from '@/components/shared/MatterPaymentStory';
import type { FinancialActionType } from '@/lib/mattersApi';
import { MatterActivitiesPanel } from '@/components/matter-dashboard/MatterActivitiesPanel';
import { MatterHistoryPanel } from '@/components/matter-dashboard/MatterHistoryPanel';
import { MatterHealthCard } from '@/components/matter-dashboard/MatterHealthCard';
import { DocumentPreviewModal } from '@/components/shared/DocumentPreviewModal';
import { UploadDocumentModal } from '@/components/shared/UploadDocumentModal';
import { ChatTeardropDots, ChatsCircle, ShieldWarning } from '@phosphor-icons/react';
import { recheckConflicts } from '@/lib/conflictsApi';
import { MatterConversationsPanel } from '@/components/matter-dashboard/MatterConversationsPanel';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { useMatterQuickAccessStore } from '@/lib/matterQuickAccessStore';
import { PushPin, EnvelopeSimple, CheckCircle as CheckCircleIcon, Warning as WarningIcon, ArrowBendUpLeft, Spinner, Paperclip } from '@phosphor-icons/react';
import { fetchEmailThreadsForMatter, fetchMatterSyncedEmails, type EmailThread, type EmailStatus, type MatterSyncedEmail } from '@/lib/emailApi';
import { canMutateFinancials, canDeleteDocument, canRecordHearingOutcome, canUpdateMatter, canMutateNotes } from '@/lib/pupilUiBoundary';
import { ScrollLock } from '@/components/shared/ScrollLock';


// ─────────────────────────────────────────────────────────────
// Unified timeline event shape — built at runtime from real data
// ─────────────────────────────────────────────────────────────

interface TimelineEvent {
  id: string;
  when: string;
  whenISO: string;
  kind: 'opened' | 'hearing' | 'document' | 'note' | 'fee_note' | 'task' | 'communication' | 'status' | 'meeting';
  title: string;
  detail?: string;
  actor?: string;
  meta?: string;
  link?: string;
}

// ─────────────────────────────────────────────────────────────

type Tab = 'overview' | 'timeline' | 'hearings' | 'documents' | 'notes' | 'activities' | 'fees' | 'tasks' | 'conversations' | 'messages' | 'history' | 'email';

const VALID_TABS: Tab[] = ['overview', 'timeline', 'hearings', 'documents', 'notes', 'activities', 'fees', 'tasks', 'conversations', 'messages', 'history', 'email'];

/**
 * `NOTES_ROLES` from notes.routes.ts — who may READ a matter note at all. Notes are privileged, so
 * clerks, admins and staff are refused outright. The server then filters `pupil` out of this same
 * list to get `NOTES_WRITE_ROLES`; see `canWriteNotes` below, which applies both steps in order.
 */
const NOTE_READ_TYPES: string[] = ['silk', 'junior', 'pupil', 'door_tenant'];

export default function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Try the store first (fast if already loaded), then fall back to a direct API fetch.
  // This fixes the "Matter not found" flash when navigating directly to a URL
  // before the store has hydrated.
  const storeLoading = useMattersStore((s) => s.loading);
  const storeMatter = useMattersStore((s) => s.matters.find((m) => m.id === id));
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);

  // Direct fetch by ID — fires whenever the store doesn't have the matter
  const { data: apiFetchedMatter, isLoading: apiFetching } = useQuery({
    queryKey: ['matter-by-id', id],
    queryFn: () => fetchMatter(id!),
    enabled: !!id && !storeMatter,
    staleTime: 30_000,
    retry: 1,
  });

  const matter = storeMatter ?? apiFetchedMatter;
  const matterLoading = storeLoading || apiFetching;
  const updateMatter = useMattersStore((s) => s.updateMatter);

  // Quick access tracking (pin + recent)
  const { pinMatter, unpinMatter, isPinned, trackAccess } = useMatterQuickAccessStore();
  const isThisPinned = isPinned(id ?? '');

  // Track access when matter loads
  useEffect(() => {
    if (matter && id) {
      trackAccess({ id, name: matter.name, number: matter.number ?? '' });
    }
  }, [matter?.id, id, trackAccess, matter?.name, matter?.number]);
  const initialTab = searchParams.get('tab');
  const [tab, setTab] = useState<Tab>(VALID_TABS.includes(initialTab as Tab) ? (initialTab as Tab) : 'overview');
  const [expandedHearing, setExpandedHearing] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  // Set when the fee-record modal is editing an existing record rather than creating one.
  const [editingFeeRecord, setEditingFeeRecord] = useState<TimeEntry | null>(null);
  const [workspaceConf, setWorkspaceConf] = useState<Conference | null>(null);
  // `?close=1` — arriving from a list's Edit form where a closing status was chosen. The lists
  // have no close dialog of their own, so the ceremony happens here: one close flow, wherever
  // the journey started. The parameter is cleared so a refresh does not reopen it.
  const [closeMatterOpen, setCloseMatterOpen] = useState(searchParams.get('close') === '1');
  useEffect(() => {
    if (searchParams.get('close') === '1') {
      setCloseMatterOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('close');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [recordOutcomeHearing, setRecordOutcomeHearing] = useState<any>(null);
  const [outcomeText, setOutcomeText] = useState('');
  const [savingOutcome, setSavingOutcome] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [feeNoteModalOpen, setFeeNoteModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [partyModalOpen, setPartyModalOpen] = useState(false);
  const [showAllParties, setShowAllParties] = useState(false);
  const [timelineFilter, setTimelineFilter] = useState<'All' | 'Hearings' | 'Documents' | 'Tasks' | 'Fees' | 'Notes' | 'Comms'>('All');
  const [noteText, setNoteText] = useState('');
  // Editing one of your own notes in place (PATCH /notes/:id — author only, enforced by the server).
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState('');
  const [noteBusyId, setNoteBusyId] = useState<string | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editTaskTarget, setEditTaskTarget] = useState<Task | null>(null);
  const [detailTask, setDetailTask] = useState<Task | null>(null);
  const [completeTarget, setCompleteTarget] = useState<Task | null>(null);
  const [docCategoryFilter, setDocCategoryFilter] = useState('');
  const [readinessOpen, setReadinessOpen] = useState(false);

  const queryClient = useQueryClient();

  // Real per-matter data — replaces the previous mock arrays. Each query
  // is keyed on the matter id so route changes refetch cleanly.
  const notesQ = useQuery({
    queryKey: ['matter-notes', id],
    queryFn: () => fetchNotes({ caseId: id ?? '' }),
    enabled: !!id,
  });
  const tasksQ = useQuery({
    queryKey: ['matter-tasks', id],
    queryFn: () => fetchTasks({ caseId: id ?? '', limit: 100, sortBy: 'dueDate', sortOrder: 'asc' }),
    enabled: !!id,
  });
  const documentsQ = useQuery({
    queryKey: ['matter-documents', id],
    queryFn: () => fetchDocuments(id ?? ''),
    enabled: !!id,
  });
  // Canonical hearing readiness (Rule #14): the ONE definition, computed server-side in
  // matters.service.getHearingReadiness. The readiness strip below consumes this — it must not
  // recompute the checks locally, or the two drift (docs/fee/tasks/listing disagreeing on one page).
  const readinessQ = useQuery({
    queryKey: ['hearing-readiness', id],
    queryFn: () => fetchHearingReadiness(id ?? ''),
    staleTime: 120_000,
    enabled: !!id,
  });
  const feeRecordsQ = useQuery({
    queryKey: ['matter-fee-records', id],
    queryFn: () => fetchFeeRecords({ caseId: id ?? '' }),
    enabled: !!id,
  });
  // Single source of truth for all financial totals — used everywhere on this page
  const financialSummaryQ = useQuery({
    queryKey: ['matter-financial-summary', id],
    queryFn: () => fetchMatterFinancialSummary(id ?? ''),
    enabled: !!id,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
  const feeNotesQ = useQuery({
    queryKey: ['matter-fee-notes', id],
    queryFn: () => fetchFeeNotes({ caseId: id ?? '', limit: 100 }),
    enabled: !!id,
  });
  const matterRolesQ = useQuery({
    queryKey: ['matter-roles', id],
    queryFn: () => fetchMatterRoles(id ?? ''),
    enabled: !!id,
  });
  const conferencesQ = useQuery({
    queryKey: ['matter-conferences', id],
    queryFn: () => fetchConferences({ caseId: id ?? '', limit: 20 }),
    enabled: !!id,
    staleTime: 30_000,
  });
  // Diary events linked to this matter (meetings/travel/etc.) — hearings & conferences
  // are already shown from their own sources, so those kinds are excluded below.
  const matterEventsQ = useQuery({
    queryKey: ['matter-calendar-events', id],
    queryFn: () => fetchCalendarEvents({ caseId: id ?? '' }),
    enabled: !!id,
    staleTime: 30_000,
  });
  const unbilledRecordsQ = useQuery({
    queryKey: ['matter-unbilled', id],
    queryFn: () => fetchFeeRecords({ caseId: id ?? '', unbilled: 'true' }),
    enabled: !!id && tab === 'fees',
    staleTime: 30_000,
  });
  const paymentsQ = useQuery({
    queryKey: ['matter-payments', id],
    // includeVoided: the list endpoint defaults to hiding voided payments
    // (payments.schema.ts:78-81 → `voidedAt: null` in the WHERE). Without it a payment
    // simply VANISHES the moment it is voided — the barrister gets no confirmation that the
    // correction landed, and this table's own Voided badge and dimmed row (below) could never
    // render. An existing query option, not a new capability.
    queryFn: () => fetchPayments({ caseId: id ?? '', limit: 50, includeVoided: 'true' }),
    enabled: !!id && tab === 'fees',
    staleTime: 30_000,
  });
  const feesActivitiesQ = useQuery({
    queryKey: ['matter-fees-activities', id],
    queryFn: () => fetchActivities({ caseId: id ?? '', limit: 100 }),
    enabled: !!id && tab === 'fees',
    staleTime: 30_000,
  });
  const creditNotesQ = useQuery({
    queryKey: ['credit-notes', { caseId: id }],
    queryFn: () => fetchCreditNotes({ caseId: id ?? '' }),
    enabled: !!id && tab === 'fees',
    staleTime: 30_000,
  });
  // Shares cache with MessagingView (same key) — invalidated by WS onMessage
  const matterThreadsQ = useQuery({
    queryKey: ['messaging', 'threads', id, undefined],
    queryFn: () => fetchThreads({ matterId: id ?? '' }),
    enabled: !!id,
    staleTime: 30_000,
    refetchInterval: 120_000,
  });
  const unreadThreadCount = (matterThreadsQ.data ?? []).reduce(
    (s, t) => s + (t.unreadCount ?? 0), 0,
  );

  // Hearings from shared store — re-fetch on WebSocket calendar update so
  // any hearing created or modified by another user appears instantly.
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  const storeUpdateHearing = useHearingsStore((s) => s.updateHearing);
  const wsConnected = useWebSocketStore((s) => s.connected);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => { fetchHearings(); });
    return unsub;
  }, [wsConnected, fetchHearings]);
  const storeHearings = useHearingsStore((s) =>
    s.hearings
      .filter((h) => h.matterId === (id ?? '') && !h.id.startsWith('temp-'))
      .slice()
      .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time))
  );

  // Auto-expand the first upcoming hearing when hearings load (once only)
  const todayIso = new Date().toISOString().slice(0, 10);
  useEffect(() => {
    if (storeHearings.length === 0 || expandedHearing !== null) return;
    const first = storeHearings
      .filter((h) => h.date >= todayIso && h.status !== 'vacated')
      .sort((a, b) => a.date.localeCompare(b.date))[0];
    if (first) setExpandedHearing(first.id);
  }, [storeHearings]); // eslint-disable-line react-hooks/exhaustive-deps

  const realNotes = notesQ.data?.notes ?? [];
  const realTasks = tasksQ.data?.tasks ?? [];
  const realDocuments = documentsQ.data ?? [];
  const realFeeRecords = feeRecordsQ.data ?? [];
  const realFeeNotes = feeNotesQ.data?.feeNotes ?? [];
  const realConferences = conferencesQ.data?.conferences ?? [];
  const scheduledConferences = realConferences.filter(
    (c) => c.scheduledAt && !c.cancelledAt && !c.completedAt
  );
  const completedConferences = realConferences.filter((c) => !!c.completedAt);

  // Build a real Key-people list from the matter + matter-roles queries.
  // Court / judge sit on the matter directly; the contacts (lay client,
  // solicitor, opponent) come back via fetchMatterRoles. Order matters —
  // most-important roles (lay client, solicitor, lead) render first.
  type PartyRow = { id: string; role: string; name: string; org: string; contactId: string; contactType: 'lay' | 'solicitor' | 'barrister' | 'expert' | 'court' | 'judge' | 'other' };
  const realParties: PartyRow[] = [];
  const roles = matterRolesQ.data;
  if (matter && roles) {
    if (roles.layClientId) {
      realParties.push({ id: 'p-lay', role: 'Lay Client', name: roles.layClientName, org: '', contactId: roles.layClientId, contactType: 'lay' });
    }
    if (roles.instructingSolicitorId) {
      realParties.push({ id: 'p-sol', role: 'Instructing Solicitor', name: roles.instructingSolicitorName, org: '', contactId: roles.instructingSolicitorId, contactType: 'solicitor' });
    }
    if (matter.barrister) {
      realParties.push({ id: 'p-lead', role: 'Lead Counsel', name: matter.barrister, org: '', contactId: '', contactType: 'barrister' });
    }
    if (roles.juniorBarristerId) {
      realParties.push({ id: 'p-jun', role: 'Junior Counsel', name: roles.juniorBarristerName, org: '', contactId: roles.juniorBarristerId, contactType: 'barrister' });
    }
    if (roles.opponentContactId) {
      realParties.push({ id: 'p-opp', role: 'Opponent', name: roles.opponentContactName, org: '', contactId: roles.opponentContactId, contactType: 'other' });
    }
    if (matter.court) {
      realParties.push({ id: 'p-court', role: 'Court', name: matter.court, org: '', contactId: '', contactType: 'court' });
    }
    if (matter.judge) {
      realParties.push({ id: 'p-judge', role: 'Judge', name: matter.judge, org: '', contactId: '', contactType: 'judge' });
    }
  }

  const realActivities = feesActivitiesQ.data?.activities ?? [];
  // Financial totals from the canonical summary endpoint — single source of truth.
  // Falls back to zero while loading so tiles never show stale values.
  const fin = financialSummaryQ.data;

  // Every tab below renders `query.data && rows.length === 0 ? "No … yet"`, and money falls back to
  // £0 — so a section that failed to load is either blank or reads as empty/zero. Name the failures.
  // A 401/403/404 is a deliberate refusal (e.g. a clerk is never shown privileged notes), not a
  // connection problem — counting it would put a false banner on every matter a clerk opens.
  const statusOf = (q: { error: unknown }) =>
    (q.error as { response?: { status?: number } } | null)?.response?.status;
  const isLoadFailure = (q: { isError: boolean; error: unknown }) => {
    const status = statusOf(q);
    return q.isError && status !== 401 && status !== 403 && status !== 404;
  };
  // Excluding a 403 from the banner above is right, but it must then be SAID somewhere, or the
  // section just renders empty and reads as fact. The Fees tab's payments table is the one place
  // that happens for a pupil (`GET /payments` → 403); see its refusal row.
  const paymentsRefused = paymentsQ.isError && statusOf(paymentsQ) === 403;
  const failedSections = ([
    [notesQ, 'notes'], [tasksQ, 'tasks'], [documentsQ, 'documents'], [feeRecordsQ, 'fee records'],
    [financialSummaryQ, 'financial summary'], [feeNotesQ, 'fee notes'], [matterRolesQ, 'parties'],
    [conferencesQ, 'conferences'], [matterEventsQ, 'timeline'], [paymentsQ, 'payments'],
    [creditNotesQ, 'credit notes'], [matterThreadsQ, 'messages'],
  ] as const).filter(([q]) => isLoadFailure(q));
  const recordedTotal    = fin?.recorded    ?? 0;
  const billedTotal      = fin?.billed      ?? 0;
  const collectedTotal   = fin?.collected   ?? 0;
  const outstandingTotal = fin?.outstanding ?? 0;

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.memberType === 'admin';
  const isJuniorClerk = user?.memberType === 'junior_clerk';
  const basePath = isAdmin ? '/admin' : user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk' ? '/clerk' : '/barrister';
  const chambersMode = useChambersMode();
  const isBarristerView = basePath === '/barrister';
  // Mirrors CONFLICT_ROLES on POST /conflicts/:caseId/recheck (pupils and door tenants are refused).
  const canRecheckConflicts = ['admin', 'senior_clerk', 'junior_clerk', 'silk', 'junior'].includes(user?.memberType ?? '');
  const [recheckBusy, setRecheckBusy] = useState(false);
  // Junior clerks were the only exclusion, which silently admitted PUPILS: they are not junior
  // clerks, so every fee/time-entry control was drawn for them and then 403'd on submit (fees and
  // activities `WRITE_ROLES` both exclude pupil). Pupils are read-only on money.
  const canEditFinancials = !isJuniorClerk && canMutateFinancials(user?.memberType);
  // Mirrors NOTES_WRITE_ROLES on the notes API, in the same two steps the server takes:
  //   NOTES_ROLES       = silk · junior · pupil · door_tenant      (notes are privileged —
  //                                                                clerks and admins are refused)
  //   NOTES_WRITE_ROLES = NOTES_ROLES.filter((r) => r !== 'pupil')  (notes.routes.ts:24)
  // The pupil half now comes from the shared boundary module rather than being implied by a
  // hand-written list, so it is pinned by that module's test alongside every other pupil gate.
  const canWriteNotes =
    NOTE_READ_TYPES.includes(user?.memberType ?? '') && canMutateNotes(user?.memberType);
  // A pupil holds no `case.update.*`, so close / archive / restore all answer 403.
  const canChangeMatter = canUpdateMatter(user?.memberType);

  // ── Void gates: mirror the BACKEND authorization exactly, not `canEditFinancials` ──────────
  //
  // The two endpoints do NOT agree with each other, and neither agrees with `canEditFinancials`
  // (which merely excludes junior clerks). Showing a control the server will refuse is a
  // dead-end; hiding one the server allows removes a correction path the role is entitled to.
  // So each mirrors its own route:
  //
  //   payments.routes.ts:25-28,37   WRITE_ROLES = admin · senior_clerk · junior_clerk · silk ·
  //                                 junior · door_tenant   (every internal role except pupil)
  //   credit-notes.routes.ts:22,31  VOID_ROLES  = admin ONLY, plus requireClerkPermission
  //                                 ('MANAGE_FEES') — which passes non-clerks straight through
  //                                 (clerk-permission.middleware.ts:32-33), so for an admin the
  //                                 effective gate is the member type alone.
  //
  // The server remains authoritative either way: these only decide whether the control is drawn.
  const PAYMENT_VOID_ROLES = ['admin', 'senior_clerk', 'junior_clerk', 'silk', 'junior', 'door_tenant'];
  const canVoidPayment = !!user?.memberType && PAYMENT_VOID_ROLES.includes(user.memberType);
  const canVoidCreditNote = isAdmin;

  // ── Voiding money ─────────────────────────────────────────────────────────────────────────
  // The ONLY callers of POST /payments/:id/void and POST /credit-notes/:id/void. Both endpoints
  // and both web wrappers already existed and had no caller at all, so a payment recorded
  // against the wrong fee note, or a credit note issued in error, was permanent from inside the
  // product. Nothing here computes money: the server reverses the allocation effect under a row
  // lock and re-derives the fee-note status through the canonical resolver. This only asks.
  const [voidPaymentTarget, setVoidPaymentTarget] = useState<{ id: string; amount: string; isWriteOff: boolean } | null>(null);
  const [voidCreditNoteTarget, setVoidCreditNoteTarget] = useState<{ id: string; number: string } | null>(null);

  /** Every money surface on this page reads one of these; a void moves all of them. */
  const invalidateMoney = () => {
    // Keys verified against their owners, not guessed: the payment/credit-note tables on this
    // page, and MatterMoneySituation / MatterPaymentStory which render the outstanding balance
    // and the payment story beside them. A void changes every one of those figures.
    // The second group are the off-page surfaces the same void moves — the fee-note lists, the
    // fees activity feed, and the aged-debt / unallocated-funds report tiles. All are live query
    // keys elsewhere in the app; leaving them out left those screens showing the pre-void figures.
    for (const key of [
      ['matter-payments'],
      ['credit-notes'],
      ['matter-payment-story'],
      ['matter-fee-notes'],
      ['matter-financial-state'],
      ['matter-financial-summary'],
      ['matter-unbilled'],
      ['fee-notes'],
      ['matter-fees'],
      ['matter-fees-activities'],
      ['open-invoices'],
      ['aged-debt'],
      ['unallocated-funds'],
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  const voidPaymentMut = useMutation({
    mutationFn: ({ paymentId, reason }: { paymentId: string; reason: string; isWriteOff: boolean }) =>
      voidPayment(paymentId, reason),
    onSuccess: (_data, { isWriteOff }) => {
      toast.success(isWriteOff ? 'Write-off reversed' : 'Payment voided');
      setVoidPaymentTarget(null);
      invalidateMoney();
    },
    onError: (err: unknown, { isWriteOff }) =>
      toast.error(getApiErrorMessage(err, isWriteOff ? 'Could not reverse the write-off' : 'Could not void the payment')),
  });

  const voidCreditNoteMut = useMutation({
    mutationFn: ({ creditNoteId, voidReason }: { creditNoteId: string; voidReason: string }) =>
      voidCreditNote(creditNoteId, { voidReason }),
    onSuccess: () => {
      toast.success('Credit note voided');
      setVoidCreditNoteTarget(null);
      invalidateMoney();
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not void the credit note')),
  });

  // Shared handler for the money-situation card's recommended action (used on Overview + Fees).
  const onMoneyAction = (type: FinancialActionType) => {
    if (type === 'raise_fee_note') setFeeNoteModalOpen(true);
    else if (type === 'close_matter') setCloseMatterOpen(true);
    else if (type === 'follow_up' || type === 'call_solicitor') {
      setTab('fees');
      toast.message(
        type === 'call_solicitor'
          ? 'Call the solicitor, then log it against the fee note.'
          : 'Follow up personally, then log it against the fee note.',
      );
    }
  };
  // Solo barristers don't use the allocation handshake — they manage their own matters directly
  const showAllocationCard = !isBarristerView || !chambersMode.isSolo;

  // All active chambers clerks for the designated-clerk selector.
  // Uses fetchUsers (all chambers users) so any new hire appears immediately
  // without requiring a prior BarristerClerk link.
  const clerksQ = useQuery({
    queryKey: ['chambers-clerks'],
    queryFn: () => fetchUsers({ status: 'active', limit: 100 }),
    enabled: isBarristerView,
    staleTime: 5 * 60 * 1000,
  });
  const chambersClerks: ApiUser[] = (clerksQ.data ?? []).filter(
    (u) => u.memberType === 'senior_clerk' || u.memberType === 'junior_clerk',
  );

  // ── Memos before the not-found guard (Rules of Hooks: no hooks after conditional returns) ──

  // All hearings for this matter from the store — always safe, storeHearings is always an array.
  const allHearings = useMemo(() => {
    return storeHearings.map((sh) => ({
      id: sh.id,
      date: new Date(sh.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      time: sh.time,
      type: sh.type,
      judge: sh.judge,
      courtroom: sh.courtroom || sh.court,
      duration: sh.durationLabel,
      status: sh.status,
      counselPresent: [sh.leadBarrister, sh.juniorBarrister].filter(Boolean),
      outcome: sh.outcome,
      outcomeType: sh.outcomeType ?? '',
      orders: sh.orders ? sh.orders.split('\n').filter(Boolean) : [],
      counselNote: sh.counselNotes,
      judicialRemarks: sh.judicialRemarks ?? '',
      nextHearingDate: sh.nextHearingDate
        ? new Date(sh.nextHearingDate + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
        : '',
      costsPosition: sh.costsPosition ?? '',
      deadlines: sh.deadlines ?? '',
      hearingResult: sh.hearingResult ?? '',
      updatedBy: sh.updatedBy ?? '',
      updatedAt: sh.updatedAt,
    }));
  }, [storeHearings]);

  // Synced inbox/sent mail linked to this matter — shares the React Query cache
  // with MatterEmailPanel (same key) so it is fetched once. Feeds both the Email
  // tab and the timeline aggregation below, so a linked email shows on the matter.
  const { data: linkedEmails = [] } = useQuery<MatterSyncedEmail[]>({
    queryKey: ['matter-synced-emails', matter?.id],
    queryFn: () => fetchMatterSyncedEmails(matter!.id),
    enabled: !!matter?.id,
    staleTime: 30_000,
  });

  // Build a unified timeline by aggregating real notes / fee records / fee
  // notes / documents / tasks / hearings / linked emails + the matter's
  // openedDate. Sorted most-recent first. This is a client-side aggregation; a
  // server-side /matters/:id/timeline endpoint can replace it without a UI change.
  const realTimeline = useMemo<TimelineEvent[]>(() => {
    if (!matter) return [];
    const fmt = (d: Date) => d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const fmtDate = (d: Date) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const events: TimelineEvent[] = [];

    // Matter opened
    if (matter.createdAt) {
      const d = new Date(matter.createdAt);
      events.push({
        id: 'opened',
        when: fmt(d),
        whenISO: d.toISOString(),
        kind: 'opened',
        title: 'Matter opened',
        actor: matter.createdBy,
      });
    }

    for (const n of realNotes) {
      const d = new Date(n.createdAt);
      const firstLine = n.body.split('\n')[0]?.slice(0, 120) ?? '';
      events.push({
        id: `note-${n.id}`,
        when: fmt(d),
        whenISO: d.toISOString(),
        kind: 'note',
        title: 'Note posted',
        detail: firstLine,
        actor: authorName(n),
      });
    }

    for (const f of realFeeRecords) {
      // f.createdAt is a number (epoch); fall back if missing.
      const d = new Date(f.createdAt);
      events.push({
        id: `fee-${f.id}`,
        when: fmt(d),
        whenISO: d.toISOString(),
        kind: 'fee_note',
        title: `${f.kind}${f.barrister ? ` — ${f.barrister}` : ''}`,
        detail: `£${f.amount.toLocaleString()}${f.hours ? ` · ${f.hours}h` : ''}`,
        actor: f.barrister || undefined,
      });
    }

    for (const fn of realFeeNotes) {
      const d = new Date(fn.issueDate);
      events.push({
        id: `feenote-${fn.id}`,
        when: fmtDate(d),
        whenISO: d.toISOString(),
        kind: 'fee_note',
        title: `Fee note ${fn.feeNoteNumber}`,
        detail: `£${Number(fn.total).toLocaleString('en-GB', { maximumFractionDigits: 2 })} · ${FEE_NOTE_STATUS_LABEL[fn.status as FeeNoteStatus] ?? fn.status}`,
      });
    }

    for (const doc of realDocuments) {
      // documentsApi already formats `uploaded` as a date string; we don't
      // have the raw createdAt here, so use the formatted form for display
      // but keep ISO sortability via Date parse.
      const parsed = Date.parse(doc.uploaded);
      const iso = isNaN(parsed) ? new Date(0).toISOString() : new Date(parsed).toISOString();
      events.push({
        id: `doc-${doc.id}`,
        when: doc.uploaded,
        whenISO: iso,
        kind: 'document',
        title: doc.name,
        detail: `${doc.kind} · ${doc.size}`,
        actor: doc.by || undefined,
      });
    }

    for (const t of realTasks) {
      if (t.status === 'completed' && t.completedAt) {
        const d = new Date(t.completedAt);
        events.push({
          id: `task-done-${t.id}`,
          when: fmt(d),
          whenISO: d.toISOString(),
          kind: 'task',
          title: `Task completed: ${t.title}`,
          actor: t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}` : undefined,
        });
      }
    }

    for (const h of allHearings) {
      // Hearing display date is human-readable; reconstruct sortable ISO from it.
      const parsed = Date.parse(h.date);
      const iso = isNaN(parsed) ? new Date(0).toISOString() : new Date(parsed).toISOString();
      events.push({
        id: `hearing-${h.id}`,
        when: `${h.date} · ${h.time}`,
        whenISO: iso,
        kind: 'hearing',
        title: `${h.type}${h.status === 'completed' ? ' heard' : ''}`,
        detail: [h.judge, h.courtroom].filter(Boolean).join(' · '),
        meta: h.counselPresent.join(' + '),
      });
    }

    for (const e of linkedEmails) {
      const when = e.receivedAt ?? e.sentAt;
      if (!when) continue;
      const d = new Date(when);
      const isOutbound = e.direction === 'outbound';
      const counterparty = isOutbound ? (e.toAddresses[0] ?? '') : e.fromAddress;
      events.push({
        id: `email-${e.id}`,
        when: fmt(d),
        whenISO: d.toISOString(),
        kind: 'communication',
        title: `Email ${isOutbound ? 'sent to' : 'received from'} ${senderName(counterparty)}`,
        detail: e.subject || '(no subject)',
        meta: e.attachmentCount > 0 ? `${e.attachmentCount} attachment${e.attachmentCount !== 1 ? 's' : ''}` : undefined,
      });
    }

    // Matter-linked diary events (meetings, travel, etc.) — exclude hearing/conference
    // kinds, which are already on the timeline from their own sources (no duplicates).
    for (const ev of (matterEventsQ.data ?? [])) {
      if (ev.kind === 'hearing' || ev.kind === 'conference') continue;
      const iso = new Date(`${ev.date}T${ev.startTime || '00:00'}:00`).toISOString();
      events.push({
        id: `event-${ev.id}`,
        when: `${ev.date}${ev.startTime ? ` · ${ev.startTime}` : ''}`,
        whenISO: iso,
        kind: 'meeting',
        title: ev.title,
        detail: [KIND_LABEL[ev.kind] ?? ev.kind, ev.location].filter(Boolean).join(' · '),
        meta: ev.notes || undefined,
      });
    }

    return events.sort((a, b) => b.whenISO.localeCompare(a.whenISO));
  }, [matter, realNotes, realFeeRecords, realFeeNotes, realDocuments, realTasks, allHearings, linkedEmails, matterEventsQ.data]);

  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['matter-tasks', id] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    queryClient.invalidateQueries({ queryKey: ['diary-tasks'] });
  };

  const completeTaskMut = useMutation({
    mutationFn: async ({ taskId, note, file }: { taskId: string; note: string; file?: File }) => {
      await updateTaskApi(taskId, { status: 'completed', completionNote: note });
      if (file && id) {
        await uploadDocumentApi(file, { caseId: id, category: 'task_completion' });
      }
    },
    onSuccess: () => {
      toast.success('Task completion recorded');
      setCompleteTarget(null);
      setDetailTask(null);
      invalidateTasks();
    },
    onError: () => toast.error('Could not record completion'),
  });

  const reopenTaskMut = useMutation({
    mutationFn: (taskId: string) => updateTaskApi(taskId, { status: 'pending' }),
    onSuccess: () => { toast.success('Task reopened'); setDetailTask(null); invalidateTasks(); },
    onError: () => toast.error('Could not reopen task'),
  });

  const deleteTaskMut = useMutation({
    mutationFn: async (taskId: string) => {
      const { deleteTask } = await import('@/lib/tasksApi');
      return deleteTask(taskId);
    },
    onSuccess: () => { toast.success('Task deleted'); setDetailTask(null); invalidateTasks(); },
    onError: () => toast.error('Could not delete task'),
  });

  // ── Not-found guard (after all hooks and memos) ──
  if (!matter) {
    if (matterLoading) {
      return (
        <div className="flex items-center justify-center py-32">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
            <span className="text-sm">Loading matter…</span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <div className="text-lg font-semibold text-foreground">Matter not found</div>
        <p className="mt-1 text-sm text-muted-foreground">Matter <span className="text-xs">{id}</span> does not exist or you do not have access.</p>
        <Button variant="outline" size="md" className="mt-6" leftIcon={<ChevronLeft className="h-4 w-4" />} onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div>
      {failedSections.length > 0 && (
        <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>
            Couldn't load {failedSections.map(([, label]) => label).join(', ')} — those sections may look empty or show £0. This is a connection problem, not missing data.
          </span>
          <Button variant="outline" size="sm" onClick={() => failedSections.forEach(([q]) => { void q.refetch(); })}>
            Try again
          </Button>
        </div>
      )}
      <PageHeader
        eyebrow={<span className="">{matter.number}</span>}
        title={toTitleCase(matter.name)}
        subtitle={`${matter.area}${matter.subArea ? ` · ${matter.subArea}` : ''}`}
        onBack={() => navigate(-1)}
        backLabel="Back to matters"
        actions={
          <>
            <Button
              variant={isThisPinned ? 'primary' : 'outline'}
              size="md"
              leftIcon={<PushPin className="h-4 w-4" weight={isThisPinned ? 'fill' : 'regular'} />}
              onClick={() => {
                if (isThisPinned) {
                  unpinMatter(matter.id);
                  toast.success('Matter unpinned');
                } else {
                  pinMatter({ id: matter.id, name: matter.name, number: matter.number ?? '' });
                  toast.success('Matter pinned — find it on the Matters page');
                }
              }}
            >
              {isThisPinned ? 'Pinned' : 'Pin'}
            </Button>
            <Button variant="outline" size="md" leftIcon={<Edit className="h-4 w-4" />} onClick={() => setEditOpen(true)}>Edit</Button>
            {canRecheckConflicts && (
              <Button
                variant="outline"
                size="md"
                leftIcon={<ShieldWarning className="h-4 w-4" />}
                disabled={recheckBusy}
                onClick={async () => {
                  setRecheckBusy(true);
                  try {
                    const r = await recheckConflicts(matter.id);
                    if (r.criticalCount > 0) {
                      toast.warning(
                        `Critical conflict found (${r.criticalCount}). A conflict review has been raised for an authorised decision.`,
                        { duration: 8000 },
                      );
                    } else if (r.hasConflicts) {
                      toast.info(`${r.warningCount + r.infoCount} possible conflict${r.warningCount + r.infoCount === 1 ? '' : 's'} noted — nothing critical.`);
                    } else {
                      toast.success('Conflict check re-run — no conflicts found');
                    }
                    void queryClient.invalidateQueries({ queryKey: ['matter-by-id', id] });
                  } catch (err) {
                    toast.error(getApiErrorMessage(err, 'Could not re-run the conflict check'));
                  } finally {
                    setRecheckBusy(false);
                  }
                }}
              >
                {recheckBusy ? 'Checking…' : 'Re-run conflict check'}
              </Button>
            )}
            <Button variant="outline" size="md" leftIcon={<Calendar className="h-4 w-4" />} onClick={() => navigate(`${basePath}/hearings/new?matter=${matter.id}`)}>
              Schedule hearing
            </Button>
            {/* Lifecycle actions — all three POST to a `case.update.*` route, which a pupil does
                not hold, so each one answered 403 (archive and restore after an optimistic
                "Matter archived" / "restored" toast). Withheld rather than left to fail. */}
            {canChangeMatter && !['closed_won','closed_lost','closed_settled','withdrawn','discontinued','archived'].includes(matter.status) && (
              <Button variant="outline" size="md" onClick={() => setCloseMatterOpen(true)}>
                Close matter
              </Button>
            )}
            {canChangeMatter && matter.status !== 'archived' && (
              <Button variant="outline" size="md" onClick={async () => {
                setLifecycleLoading(true);
                try { await archiveMatter(matter.id); queryClient.invalidateQueries({ queryKey: ['matter-by-id', id] }); toast.success('Matter archived'); }
                catch { toast.error('Could not archive'); }
                finally { setLifecycleLoading(false); }
              }} disabled={lifecycleLoading}>Archive</Button>
            )}
            {canChangeMatter && ['closed_won','closed_lost','closed_settled','withdrawn','discontinued','archived'].includes(matter.status) && (
              <Button variant="outline" size="md" onClick={async () => {
                setLifecycleLoading(true);
                try { await restoreMatter(matter.id); queryClient.invalidateQueries({ queryKey: ['matter-by-id', id] }); toast.success('Matter restored to active'); }
                catch { toast.error('Could not restore'); }
                finally { setLifecycleLoading(false); }
              }} disabled={lifecycleLoading}>Restore</Button>
            )}
            {canEditFinancials && (
              <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setFeeModalOpen(true)}>
                Log time
              </Button>
            )}
          </>
        }
      />

      {/* Case identity strip — matter reference, court, barrister, next hearing */}
      <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-lg border border-border/60 bg-muted/20 px-4 py-2.5 text-xs">
        <MatterStatusBadge status={matter.status} />
        {(matter.riskRating === 'High' || matter.riskRating === 'Critical') && (
          <Badge variant="warning">Risk: {matter.riskRating}</Badge>
        )}
        {matter.court && (
          <span className="flex items-center gap-1.5 text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            {matter.court}
          </span>
        )}
        {matter.next && matter.next !== '—' && (
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            Next: {matter.next}
          </span>
        )}
        {matter.barrister && (
          <span className="flex items-center gap-1.5 text-foreground">
            {matter.barrister}
            {matter.role === 'junior' && (
              <span className="text-[10px] text-muted-foreground">(junior)</span>
            )}
          </span>
        )}
        {matter.feeEstimate > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <CurrencyGbp className="h-3 w-3 shrink-0" />
            {matter.feeEstimate >= 1000
              ? `${(matter.feeEstimate / 1000).toFixed(0)}k estimate`
              : `${matter.feeEstimate.toLocaleString('en-GB')} estimate`}
          </span>
        )}
      </div>

      {/* Tabs — twelve of them, which is wider than the content area on a desktop. The strip
          scrolled sideways with the scrollbar hidden, so History and Email were cut off at the
          edge with nothing on screen saying they were there. Wide screens wrap instead, showing
          every tab; narrow ones keep the sideways scroll, which is the right gesture on a phone. */}
      <div className="no-scrollbar -mb-px mb-4 flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border lg:flex-wrap lg:overflow-visible">
        {([
          { key: 'overview',  label: 'Overview',  icon: Briefcase  },
          { key: 'timeline',  label: 'Timeline',  icon: ClockClockwise },
          { key: 'hearings',  label: `Hearings (${allHearings.length})`,  icon: Gavel },
          { key: 'documents', label: `Documents (${realDocuments.length})`, icon: FileText },
          { key: 'notes',     label: `Notes (${realNotes.length})`,         icon: NoteIcon },
          { key: 'activities', label: 'Time & Expenses', icon: ClockClockwise },
          { key: 'fees',      label: outstandingTotal > 0 ? `Fees · £${(outstandingTotal / 1000).toFixed(0)}k due` : 'Fees', icon: Wallet },
          { key: 'tasks',     label: `Tasks (${realTasks.filter((t) => t.status === 'pending').length})`, icon: ListChecks },
          { key: 'conversations', label: 'Conversations', icon: ChatsCircle },
          { key: 'messages',  label: unreadThreadCount > 0 ? `Messages · ${unreadThreadCount}` : 'Messages',  icon: ChatTeardropDots },
          { key: 'history',   label: 'History',   icon: ClockCounterClockwise },
          { key: 'email',     label: 'Email',     icon: EnvelopeSimple },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition ${
              tab === t.key
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
          {/* ── Main column ── */}
          <div className="space-y-4">
            {/* Matter situation — answers "what state is this matter in?" the way a barrister
                thinks (court soon / action needed / waiting / active), not which tab to open.
                Derived from signals already loaded on the matter; no extra fetch. */}
            {(() => {
              if (isTerminalMatterStatus(matter.status)) return null;
              const today = new Date().toISOString().slice(0, 10);
              const upcoming = storeHearings
                .filter((h) => h.date >= today && h.status !== 'vacated' && h.status !== 'completed')
                .sort((a, b) => a.date.localeCompare(b.date))[0];
              const overdue = realTasks.filter((t) => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'completed').length;
              const lastActivity = matter.updatedAt ?? matter.createdAt;
              const daysSince = lastActivity ? Math.floor((Date.now() - new Date(lastActivity).getTime()) / 86_400_000) : 0;

              let tone: 'amber' | 'red' | 'slate' | 'emerald' = 'emerald';
              let label = 'Active';
              let detail = 'This matter is on track.';
              if (upcoming) {
                const d = Math.ceil((new Date(upcoming.date + 'T00:00:00').getTime() - new Date(today + 'T00:00:00').getTime()) / 86_400_000);
                if (d <= 7) {
                  tone = 'amber';
                  label = 'Preparing for court';
                  detail = d <= 0 ? 'Hearing today.' : d === 1 ? 'Hearing tomorrow — is the brief ready?' : `Hearing in ${d} days.`;
                }
              }
              if (label === 'Active' && overdue > 0) {
                tone = 'red';
                label = 'Action needed';
                detail = `${overdue} task${overdue > 1 ? 's' : ''} overdue on this matter.`;
              }
              if (label === 'Active' && !upcoming && daysSince >= 21) {
                tone = 'slate';
                label = 'Waiting';
                detail = `Nothing has happened on this matter for ${daysSince} days — why is it not moving?`;
              }

              const toneClass: Record<typeof tone, string> = {
                amber: 'border-amber-200 bg-amber-50/50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
                red: 'border-red-200 bg-red-50/50 text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
                slate: 'border-slate-200 dark:border-slate-700 bg-slate-50 text-slate-700 dark:text-slate-300 dark:border-border dark:bg-muted dark:text-muted-foreground',
                emerald: 'border-emerald-200 bg-emerald-50/50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
              };

              return (
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border px-4 py-2.5 ${toneClass[tone]}`}>
                  <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
                  <span className="text-sm">{detail}</span>
                </div>
              );
            })()}

            {/* Quick actions — the matter is the operating centre; every frequent action is one
                click, no hunting through tabs. All reuse the modals already on this page. */}
            <div className="rounded-lg border border-border/60 bg-card p-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Quick actions
              </div>
              {/* Equal cells that fill the card, rather than a wrapping flex row.
                  `flex-wrap` sized each button to its own label, so six actions came out as
                  ragged rows of six different widths (100, 104, 105, 133, 155, 157) — and the
                  card is 327px wide in one layout and 762px in another, so a media query cannot
                  decide the column count. `auto-fill` + a 9.5rem floor lets the GRID measure the
                  card: two columns when it is narrow, four when it is wide, always equal. */}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(9rem,1fr))] gap-2">
                <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setTaskModalOpen(true)}>
                  New task
                </Button>
                <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setUploadOpen(true)}>
                  Upload document
                </Button>
                <Button variant="outline" size="sm" leftIcon={<Calendar className="h-3.5 w-3.5" />} onClick={() => navigate(`${basePath}/hearings/new?matter=${matter.id}`)}>
                  Schedule hearing
                </Button>
                {/* Only an offer if the Notes tab can actually take one — for a pupil it just
                    landed on the read-only tab and its "pupils cannot add or change them" card. */}
                {canWriteNotes && (
                  <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setTab('notes')}>
                    Add note
                  </Button>
                )}
                {canEditFinancials && (
                  <>
                    <Button variant="outline" size="sm" leftIcon={<CurrencyGbp className="h-3.5 w-3.5" />} onClick={() => setFeeModalOpen(true)}>
                      Log time
                    </Button>
                    <Button variant="outline" size="sm" leftIcon={<CurrencyGbp className="h-3.5 w-3.5" />} onClick={() => setFeeNoteModalOpen(true)}>
                      Raise fee note
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* Court situation — "Court Tomorrow / Court Today" preparation (most urgent) */}
            <MatterCourtSituation matterId={matter.id} />

            {/* Money situation — where does the money stand / what's next (intelligence) */}
            <MatterMoneySituation matterId={matter.id} canAct={canEditFinancials} onAction={onMoneyAction} />

            {/* Financial summary — WIP / Outstanding / Trust */}
            <MatterFinancialWidgets
              matterId={matter.id}
              onViewOutstanding={() => setTab('fees')}
            />

            {/* Hearing Readiness Strip — only shown when there's an upcoming hearing */}
            {(() => {
              // Canonical readiness — the checks come straight from readinessQ (server-computed),
              // NOT recomputed here. Only cosmetic labels (doc count, fee £, court) read local data.
              const checklist = readinessQ.data?.hasUpcomingHearing ? readinessQ.data.checklist : null;
              if (!checklist) return null;

              const daysUntil = checklist.daysUntil;
              const isUrgent = daysUntil <= 3;
              // Look up the SAME hearing the checks refer to, only for display extras (court/type).
              const stripHearing = storeHearings.find((h) => h.id === checklist.hearingId);
              const hearingDateLabel = new Date(checklist.hearingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
              const feeK = (matter.feeEstimate ?? 0) / 1000;

              // Outstanding count drives the collapsed summary so the strip stays useful when closed.
              const checks = [checklist.documentsUploaded, checklist.tasksComplete, checklist.feeAgreed, checklist.listingConfirmed];
              const outstanding = checks.filter((c) => !c).length;

              return (
                <div className={`rounded-lg border px-4 py-3 ${isUrgent ? 'border-red-200 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/10' : 'border-amber-200 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/10'}`}>
                  <button
                    type="button"
                    onClick={() => setReadinessOpen((v) => !v)}
                    aria-expanded={readinessOpen}
                    className="flex w-full items-center justify-between text-left"
                  >
                    <div className="flex items-center gap-2">
                      <CaretDown
                        className={`h-3.5 w-3.5 shrink-0 transition-transform ${readinessOpen ? '' : '-rotate-90'} ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}
                        weight="bold"
                      />
                      <span className={`text-xs font-bold uppercase tracking-widest ${isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
                        Hearing readiness
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isUrgent ? 'bg-red-200 text-red-900 dark:text-red-200' : 'bg-amber-200 text-amber-900 dark:text-amber-200'}`}>
                        {daysUntil <= 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                      </span>
                      {/* Collapsed summary — outstanding items at a glance without expanding */}
                      {!readinessOpen && (
                        <span className={`text-xs font-medium ${outstanding > 0 ? (isUrgent ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300') : 'text-emerald-700 dark:text-emerald-300'}`}>
                          {outstanding > 0 ? `${outstanding} item${outstanding !== 1 ? 's' : ''} outstanding` : 'All ready'}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {stripHearing?.type ?? checklist.hearingType}{stripHearing?.court ? ` · ${stripHearing.court}` : ''} · {hearingDateLabel}
                    </span>
                  </button>

                  {readinessOpen && (
                  <>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <ReadinessCheck ok={checklist.documentsUploaded} label={checklist.documentsUploaded ? `${realDocuments.length} document${realDocuments.length !== 1 ? 's' : ''} uploaded` : 'No documents uploaded'} />
                    <ReadinessCheck ok={checklist.tasksComplete} label={checklist.tasksComplete ? 'Tasks cleared' : 'Tasks outstanding'} />
                    <ReadinessCheck ok={checklist.feeAgreed} label={checklist.feeAgreed ? `Brief fee agreed (£${feeK.toFixed(0)}k)` : 'Brief fee not recorded'} />
                    <ReadinessCheck ok={checklist.listingConfirmed} label={checklist.listingConfirmed ? 'Listing confirmed' : 'Awaiting confirmation'} />
                  </div>

                  {/* The actual papers the barrister needs tonight — one click to open, no hunting
                      through the Documents tab. Answers "what am I missing?" directly. */}
                  <div className="mt-3 border-t border-border/40 pt-2.5">
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Papers for this hearing
                    </div>
                    {(() => {
                      const latest = [...realDocuments]
                        .sort((a, b) => (Date.parse(b.uploaded) || 0) - (Date.parse(a.uploaded) || 0))
                        .slice(0, 3);
                      if (latest.length === 0) {
                        return (
                          <button
                            type="button"
                            onClick={() => setUploadOpen(true)}
                            className="text-sm font-medium text-brand-700 dark:text-brand-300 underline underline-offset-2"
                          >
                            No documents yet — upload the brief
                          </button>
                        );
                      }
                      return (
                        <div className="space-y-1">
                          {latest.map((d) => (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => setPreviewDocId(d.id)}
                              className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition hover:bg-muted/50"
                            >
                              <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                              <span className="min-w-0 flex-1 truncate text-sm text-foreground">{d.name}</span>
                              <span className="shrink-0 text-xs text-muted-foreground">{d.uploaded}</span>
                            </button>
                          ))}
                          <button
                            type="button"
                            onClick={() => setTab('email')}
                            className="mt-1 px-1.5 text-xs font-medium text-brand-700 dark:text-brand-300 underline underline-offset-2"
                          >
                            View latest correspondence →
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                  </>
                  )}
                </div>
              );
            })()}

            {/* Summary */}
            <Card>
              <CardHeader className="pb-2 pt-4"><CardTitle>Summary</CardTitle></CardHeader>
              <CardContent className="pb-4">
                <p className={`max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed ${matter.summary ? 'text-foreground' : 'text-muted-foreground'}`}>{matter.summary || 'No summary recorded.'}</p>
              </CardContent>
            </Card>

            {/* Conferences — scheduled + completed with fee record auto-creation */}
            {(scheduledConferences.length > 0 || completedConferences.length > 0) && (
              <Card>
                <CardHeader className="pb-2 pt-4">
                  <div className="flex items-center gap-2">
                    <ChatCircleDots className="h-4 w-4 text-muted-foreground" />
                    <CardTitle>Conferences</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 pb-4">
                  {scheduledConferences.map((c) => {
                    const dt = new Date(c.scheduledAt!);
                    const dateStr = dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    const timeStr = dt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={c.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground">{PURPOSE_LABELS[c.purpose]}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {dateStr} · {timeStr} · {c.durationMinutes} min · {FORMAT_LABELS[c.format]}
                          </div>
                        </div>
                        <Button variant="primary" size="sm" onClick={() => setWorkspaceConf(c)}>
                          Open workspace
                        </Button>
                      </div>
                    );
                  })}
                  {completedConferences.map((c) => {
                    const dt = new Date(c.scheduledAt!);
                    const dateStr = dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
                    return (
                      <div key={c.id} className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2.5 opacity-70">
                        <CheckCircle className="h-4 w-4 shrink-0 text-emerald-500" weight="fill" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-foreground line-through">{PURPOSE_LABELS[c.purpose]}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{dateStr} · Completed · Fee record created</div>
                        </div>
                        <button
                          className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
                          onClick={() => setWorkspaceConf(c)}
                        >
                          View advice
                        </button>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {workspaceConf && (
              <ConferenceWorkspace
                conference={workspaceConf}
                basePath={basePath}
                onClose={() => setWorkspaceConf(null)}
                onChanged={() => {
                  queryClient.invalidateQueries({ queryKey: ['matter-conferences', id] });
                  queryClient.invalidateQueries({ queryKey: ['matter-fee-records', id] });
                }}
              />
            )}

            {/* Key people — compact cards instead of full table */}
            <Card>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-start justify-between">
                  <CardTitle>Key people</CardTitle>
                  <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setPartyModalOpen(true)}>Add</Button>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                {matterRolesQ.isLoading && (
                  <div className="py-2 text-sm text-muted-foreground">Loading parties…</div>
                )}
                {matterRolesQ.data && realParties.length === 0 && (
                  <div className="py-2 text-sm text-muted-foreground">
                    No parties recorded on this matter yet. Use Add to assign a lay client, solicitor or counsel.
                  </div>
                )}
                {realParties.length > 0 && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {(showAllParties ? realParties : realParties.slice(0, 6)).map((p) => (
                      <div key={p.id} className="flex items-center gap-3 rounded-md border border-border p-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-50 text-[10px] font-semibold text-brand-700 dark:text-brand-300">
                          {p.name.split(' ').map((w) => w[0]).join('').slice(0, 2)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium text-foreground">
                            {p.contactType === 'barrister' && p.contactId ? (
                              <Link to={`${basePath}/barristers/${p.contactId}`} className="text-brand-600 dark:text-brand-400 hover:underline">{p.name}</Link>
                            ) : (
                              p.name
                            )}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">{p.role}{p.org ? ` · ${p.org}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {realParties.length > 6 && (
                  <div className="mt-2 text-center">
                    <button className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline" onClick={() => setShowAllParties((v) => !v)}>
                      {showAllParties ? 'Show fewer' : `+ ${realParties.length - 6} more`}
                    </button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent activity */}
            <Card>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-start justify-between">
                  <CardTitle>Recent activity</CardTitle>
                  <Button variant="link" size="sm" onClick={() => setTab('timeline')}>View all</Button>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-1 pt-0">
                <div className="divide-y divide-border">
                  {realTimeline.length === 0 && (
                    <div className="py-2 text-xs text-muted-foreground">No recent activity yet.</div>
                  )}
                  {realTimeline.slice(0, 4).map((e) => (
                    <TimelineRow key={e.id} event={e} compact />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
            {/* Operational brief — last events, fees, team */}
            <MatterIntelligencePanel matterId={matter.id} />

            {/* Allocation handshake — hidden for solo barristers who manage their own matters */}
            {showAllocationCard && (
              <AllocationStatusCard
                caseId={matter.id}
                caseLabel={matter.name ?? matter.id}
              />
            )}

            {/* Information barriers on this matter — chambers admin only. A wall hides the OPPOSING
                matter from clerks; showing it to anyone narrower would disclose what it protects. */}
            {isAdmin && <MatterInformationBarriersPanel matterId={matter.id} />}

            {/* Phase 4: who should get this brief (clerk/admin only; self-hides otherwise) */}
            <AllocationSuggestions matterId={matter.id} />

            {/* Matter health indicators */}
            <MatterHealthCard
              matterId={matter.id}
              lastActivityDate={matter.updatedAt ?? matter.createdAt}
            />

            {/* Designated Clerk — assign any active chambers clerk to this matter */}
            {isBarristerView && (
              <Card>
                <CardHeader className="pb-2 pt-4"><CardTitle>Designated Clerk</CardTitle></CardHeader>
                <CardContent className="space-y-2 pb-4">
                  {clerksQ.isLoading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                  ) : chambersClerks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No active clerks in chambers.</p>
                  ) : (
                    <>
                      <select
                        value={matter.clerkUserId ?? ''}
                        onChange={async (e) => {
                          const clerkId = e.target.value || null;
                          const bumpVisibility = clerkId !== null && matter.visibility === 'personal';
                          try {
                            await apiUpdateMatter(matter.id, {
                              // send null (not undefined) so un-assigning actually clears the clerk
                              clerkId,
                              ...(bumpVisibility && { visibility: 'team' }),
                            });
                            updateMatter(matter.id, {
                              clerkUserId: clerkId,
                              ...(bumpVisibility && { visibility: 'team' }),
                            });
                            if (!clerkId) toast.success('Clerk unassigned');
                            else if (bumpVisibility) toast.success('Clerk designated — visibility updated to Team');
                            else toast.success('Designated clerk updated');
                          } catch {
                            toast.error('Failed to update clerk assignment');
                          }
                        }}
                        className="h-9 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
                      >
                        <option value="">— Not assigned —</option>
                        {chambersClerks.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}
                            {u.memberType === 'senior_clerk' ? ' · Senior Clerk' : ' · Junior Clerk'}
                          </option>
                        ))}
                      </select>
                      {matter.clerkUserId && matter.visibility === 'personal' && (
                        <p className="text-xs text-muted-foreground">
                          This matter is <strong>Private</strong> — only you and this designated clerk can see it; the rest of chambers cannot.
                        </p>
                      )}
                      {matter.clerkUserId && matter.visibility === 'restricted' && (
                        <p className="text-xs text-muted-foreground">
                          This matter is <strong>Restricted</strong> — hidden from other clerks unless you Allow them on this case.
                        </p>
                      )}
                      {matter.clerkUserId && matter.visibility !== 'personal' && matter.visibility !== 'restricted' && (
                        <p className="text-xs text-muted-foreground">
                          This clerk receives matter notifications and can manage documents, tasks and hearings on your behalf.
                        </p>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Per-matter clerk access: visibility (Normal/Restricted/Private) + per-clerk exceptions */}
            {isBarristerView && (
              <Card>
                <CardHeader className="pb-2 pt-4"><CardTitle>Clerk access for this matter</CardTitle></CardHeader>
                <CardContent className="pb-4">
                  <MatterAccessPanel
                    matterId={matter.id}
                    designatedClerkUserId={matter.clerkUserId ?? null}
                    onVisibilityChange={() => queryClient.invalidateQueries({ queryKey: ['matter-by-id', id] })}
                  />
                </CardContent>
              </Card>
            )}

            {/* Key details */}
            <Card>
              <CardHeader className="pb-2 pt-4"><CardTitle>Details</CardTitle></CardHeader>
              <CardContent className="space-y-2.5 pb-4">
                <DetailRow icon={<Calendar className="h-4 w-4" />} label="Opened" value={matter.createdAt ? new Date(matter.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} />
                <DetailRow icon={<Gavel className="h-4 w-4" />} label="Next" value={matter.next || '—'} mono />
                <DetailRow icon={<Building2 className="h-4 w-4" />} label="Courtroom" value={matter.courtroom || '—'} />
                {matter.judge && <DetailRow icon={<Scale className="h-4 w-4" />} label="Judge" value={matter.judge} />}
                {matter.barrister && <DetailRow icon={<Scale className="h-4 w-4" />} label="Lead counsel" value={matter.barrister} />}
                <DetailRow icon={<CurrencyGbp className="h-4 w-4" />} label="Fee estimate" value={`£${matter.feeEstimate.toLocaleString()}`} mono />
              </CardContent>
            </Card>

            {/* Financial */}
            <Card>
              <CardHeader className="pb-2 pt-4"><CardTitle>Financials</CardTitle></CardHeader>
              <CardContent className="space-y-2 pb-4">
                {[
                  { label: 'Fee estimate', value: matter.feeEstimate, bold: true },
                  { label: 'Recorded',     value: recordedTotal },
                  { label: 'Billed',       value: billedTotal },
                  { label: 'Collected',    value: collectedTotal },
                  { label: 'Outstanding',  value: outstandingTotal },
                ].map((f) => (
                  <div key={f.label} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{f.label}</span>
                    <span className={`tabular ${f.bold ? 'font-semibold' : 'font-medium'} text-foreground`}>£{f.value.toLocaleString()}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Open tasks */}
            <Card>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-start justify-between">
                  <CardTitle>Tasks</CardTitle>
                  <Button variant="link" size="sm" onClick={() => setTab('tasks')}>All</Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 pb-4">
                {tasksQ.isLoading && (
                  <div className="text-sm text-muted-foreground">Loading tasks…</div>
                )}
                {isLoadFailure(tasksQ) && (
                  <QueryErrorState compact what="tasks" onRetry={() => tasksQ.refetch()} />
                )}
                {!tasksQ.isLoading && !tasksQ.isError && realTasks.filter((t) => t.status === 'pending').length === 0 && (
                  <div className="text-sm text-muted-foreground">No open tasks on this matter.</div>
                )}
                {realTasks.filter((t) => t.status === 'pending').map((t) => (
                  <div key={t.id} className="flex items-start gap-2.5">
                    <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight text-foreground">{t.title}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {t.assignedTo ? `${t.assignedTo.firstName} ${t.assignedTo.lastName}` : 'Unassigned'}
                        {t.dueDate ? ` · Due ${new Date(t.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Intake / internal notes */}
            <Card>
              <CardHeader className="pb-2 pt-4"><CardTitle>Internal notes</CardTitle></CardHeader>
              <CardContent className="pb-4">
                <p className={`max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed ${matter.internalNotes ? 'text-foreground' : 'text-muted-foreground'}`}>{matter.internalNotes || 'No internal notes recorded.'}</p>
                {matter.createdBy && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    By {matter.createdBy}
                    {matter.createdAt ? ` · ${new Date(matter.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick actions */}
            <Card>
              <CardContent className="space-y-2 p-4">
                <Button variant="outline" size="sm" className="w-full justify-start" leftIcon={<Calendar className="h-4 w-4" />} onClick={() => navigate(`${basePath}/hearings/new?matter=${matter.id}`)}>Schedule hearing</Button>
                <Button variant="outline" size="sm" className="w-full justify-start" leftIcon={<FileText className="h-4 w-4" />} onClick={() => setTab('documents')}>Documents ({realDocuments.length})</Button>
                <Button variant="outline" size="sm" className="w-full justify-start" leftIcon={<Wallet className="h-4 w-4" />} onClick={() => setTab('fees')}>Fee records</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* TIMELINE */}
      {tab === 'timeline' && (() => {
        const TIMELINE_FILTER_KIND: Record<typeof timelineFilter, TimelineEvent['kind'] | null> = {
          All: null,
          Hearings: 'hearing',
          Documents: 'document',
          Tasks: 'task',
          Fees: 'fee_note',
          Notes: 'note',
          Comms: 'communication',
        };
        const filteredTimeline = timelineFilter === 'All'
          ? realTimeline
          : realTimeline.filter((e) => e.kind === TIMELINE_FILTER_KIND[timelineFilter]);
        return (
          <div className="space-y-4">
          <Card>
            {/* Seven filters plus a caption do not fit one phone-width row: the caption was
                squeezed to one word per line and the last pills ("Fees", "Notes", "Comms") were
                cut off the right edge with no way to reach them. Stacked up to `xl`, with the
                pills on their own swipeable strip. The breakpoint was measured, not guessed: at
                `sm` and again at `lg` the single row still cramped the caption to two lines and
                hid ~50px of pills — at `lg` because the sidebar appears and NARROWS this column.
                It becomes one row only where it genuinely fits. */}
            <div className="flex flex-col gap-2 border-b border-border px-4 py-3 sm:px-6 xl:flex-row xl:items-center xl:justify-between">
              <div className="text-sm text-muted-foreground">{filteredTimeline.length} events · ordered most recent first</div>
              <div className="no-scrollbar flex items-center gap-1 overflow-x-auto overflow-y-hidden rounded-md border border-border bg-card p-0.5 text-xs">
                {(['All', 'Hearings', 'Documents', 'Tasks', 'Fees', 'Notes', 'Comms'] as const).map((f) => (
                  <button
                    key={f}
                    onClick={() => setTimelineFilter(f)}
                    className={`shrink-0 rounded-sm px-3 py-1 font-medium ${
                      timelineFilter === f ? 'bg-brand-600 text-white' : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            <CardContent className="p-6">
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute bottom-0 left-[19px] top-0 w-px bg-border" />
                <div className="space-y-5">
                  {filteredTimeline.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">No {timelineFilter.toLowerCase()} events on this matter.</div>
                  ) : (
                    filteredTimeline.map((e) => (
                      <TimelineRow key={e.id} event={e} />
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          {/* The server's recorded chronology (GET /matters/:id/timeline) — built and hardened, never shown. */}
          <MatterCaseRecordPanel matterId={matter.id} />
          </div>
        );
      })()}

      {/* HEARINGS */}
      {tab === 'hearings' && (
        <div className="space-y-3">
          {/* On a phone the count and two buttons cannot share one row: the count wrapped to
              three lines and the buttons were squeezed beside it. Stack them, and let the pair
              share the full width — the same treatment the page header uses. */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-muted-foreground">{allHearings.length} hearings on this matter</div>
            <div className="flex gap-2 max-sm:w-full max-sm:[&>*]:flex-1">
              <Button variant="outline" size="sm" onClick={() => navigate(`${basePath}/hearings/new?matter=${matter.id}&mode=record`)}>
                Record past hearing
              </Button>
              <Button variant="primary" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => navigate(`${basePath}/hearings/new?matter=${matter.id}`)}>
                Schedule hearing
              </Button>
            </div>
          </div>
          {allHearings.map((h) => {
            const expanded = expandedHearing === h.id;
            const isPast = ['completed', 'adjourned', 'order_reserved', 'judgment_pending', 'disposed', 'vacated', 'no_show'].includes(h.status);
            return (
              <Card
                key={h.id}
                className={`overflow-hidden transition ${
                  expanded ? 'border-brand-300 shadow-pop' : 'hover:border-brand-200'
                }`}
              >
                <button
                  onClick={() => setExpandedHearing(expanded ? null : h.id)}
                  className="flex w-full items-center gap-4 px-5 py-4 text-left"
                >
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-md ${
                    ['scheduled','confirmed','in_progress','awaiting_appearance','awaiting_compliance'].includes(h.status)
                      ? 'bg-brand-50 text-brand-600 dark:text-brand-400'
                      : h.status === 'completed' || h.status === 'disposed'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
                  }`}>
                    <Gavel className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge>{h.type}</Badge>
                      {/* Real status badge */}
                      {(() => {
                        const statusMap: Record<string, { label: string; cls: string }> = {
                          scheduled:           { label: 'Scheduled',           cls: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'    },
                          confirmed:           { label: 'Confirmed',           cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'},
                          in_progress:         { label: 'In Progress',         cls: 'bg-brand-100 text-brand-700 dark:text-brand-300'    },
                          awaiting_appearance: { label: 'Awaiting Appearance', cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'    },
                          awaiting_compliance: { label: 'Awaiting Compliance', cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'    },
                          adjourned:           { label: 'Adjourned',           cls: 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300'    },
                          order_reserved:      { label: 'Order Reserved',      cls: 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300'  },
                          judgment_pending:    { label: 'Judgment Pending',    cls: 'bg-violet-100 dark:bg-violet-500/15 text-violet-700 dark:text-violet-300'  },
                          completed:           { label: 'Completed',           cls: 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'},
                          vacated:             { label: 'Vacated',             cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'        },
                          no_show:             { label: 'No Show',             cls: 'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'        },
                          disposed:            { label: 'Disposed',            cls: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'    },
                        };
                        const cfg = statusMap[h.status];
                        if (!cfg) return null;
                        return (
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${cfg.cls}`}>
                            {cfg.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="mt-1 flex items-baseline gap-3">
                      <div className="text-sm font-semibold text-foreground">{h.date} · {h.time}</div>
                      <div className="text-xs text-muted-foreground">{h.duration}</div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                      <span><Scale className="mr-1 inline h-3 w-3" />{h.judge}</span>
                      <span><MapPin className="mr-1 inline h-3 w-3" />{h.courtroom}</span>
                      <span>{h.counselPresent.join(' + ')}</span>
                    </div>
                  </div>
                  <Link
                    to={`${basePath}/hearings/${h.id}`}
                    onClick={(ev) => ev.stopPropagation()}
                    className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Open hearing detail"
                  >
                    <ArrowSquareOut className="h-4 w-4" />
                  </Link>
                </button>

                {expanded && (
                  <div className="border-t border-border/60 bg-muted/10 px-5 py-5 space-y-4">

                    {/* No outcome — show Record Outcome button for schedulable statuses.
                        A pupil may amend the listing but never record the court's result
                        (assertPupilMayNotSetOutcome on the API), so they are told, not offered. */}
                    {!h.outcome && !isPast && (
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground italic">No outcome recorded yet.</p>
                        {canRecordHearingOutcome(user?.memberType) ? (
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); setRecordOutcomeHearing(h); }}
                          >
                            Record outcome
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            The barrister on this matter records the outcome.
                          </span>
                        )}
                      </div>
                    )}

                    {/* Outcome */}
                    {h.outcome && (
                      <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <Trophy className="h-3 w-3" /> Outcome
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">{h.outcome}</p>
                      </div>
                    )}

                    {/* Orders made */}
                    {h.orders.length > 0 && (
                      <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <CheckCircle className="h-3 w-3" /> Orders made
                        </div>
                        <ul className="space-y-1">
                          {h.orders.map((o, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                              <span className="mt-[6px] h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                              {o}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Judicial remarks */}
                    {h.judicialRemarks && (
                      <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <Scale className="h-3 w-3" /> Judicial remarks
                        </div>
                        <p className="border-l-2 border-brand-200 pl-3 text-sm italic text-foreground">{h.judicialRemarks}</p>
                      </div>
                    )}

                    {/* Counsel's note */}
                    {h.counselNote && (
                      <div>
                        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          <NoteIcon className="h-3 w-3" /> Counsel's note
                        </div>
                        <p className="rounded-md border border-border bg-card px-3 py-2.5 text-sm text-foreground">
                          {h.counselNote}
                        </p>
                      </div>
                    )}

                    {/* Two-column: costs + deadlines */}
                    {(h.costsPosition || h.deadlines) && (
                      <div className="grid gap-4 sm:grid-cols-2">
                        {h.costsPosition && (
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              <CurrencyGbp className="h-3 w-3" /> Costs position
                            </div>
                            <span className="inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-xs text-foreground">
                              {h.costsPosition}
                            </span>
                          </div>
                        )}
                        {h.deadlines && (
                          <div>
                            <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                              <WarningCircle className="h-3 w-3" /> Court deadlines
                            </div>
                            <p className="text-sm text-foreground">{h.deadlines}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Next hearing */}
                    {h.nextHearingDate && (
                      <div className="flex items-center gap-2 rounded-lg border border-brand-100 bg-brand-50/60 px-3 py-2.5">
                        <ClockClockwise className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />
                        <span className="text-sm text-foreground">
                          Next hearing: <span className="font-semibold text-brand-700 dark:text-brand-300">{h.nextHearingDate}</span>
                        </span>
                      </div>
                    )}

                    {/* Footer: recorded by */}
                    {h.updatedBy && (
                      <div className="border-t border-border/50 pt-3 text-xs text-muted-foreground">
                        Recorded by {h.updatedBy}
                        {h.updatedAt ? ` \u00B7 ${new Date(h.updatedAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}` : ''}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* DOCUMENTS */}
      {tab === 'documents' && (
        <Card>
          {/* Stacked on a phone, a row from `sm` up. Wrapping a flex row left the primary
              button orphaned on its own line at its natural width, floating under the filter
              with a gap beside it. Stacking makes the filter and the button each span the
              toolbar, which reads as a toolbar rather than as two stray controls. */}
          <div className="flex flex-col gap-3 border-b border-border px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:px-6">
            <div className="flex min-w-0 items-center gap-3">
              <div className="text-sm text-muted-foreground">
                {realDocuments.length} {realDocuments.length === 1 ? 'file' : 'files'}
              </div>
              <select
                value={docCategoryFilter}
                onChange={(e) => setDocCategoryFilter(e.target.value)}
                className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-xs text-foreground sm:flex-none"
              >
                <option value="">All types</option>
                <option value="pleading">Pleading</option>
                <option value="correspondence">Correspondence</option>
                <option value="evidence">Evidence</option>
                <option value="brief">Brief</option>
                <option value="opinion">Opinion</option>
                <option value="order">Order</option>
                <option value="judgment">Judgment</option>
                <option value="draft">Draft</option>
                <option value="fee_note">Fee note</option>
                <option value="contract">Contract</option>
                <option value="other">Other</option>
              </select>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="w-full sm:w-auto"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setUploadOpen(true)}
            >
              Upload document
            </Button>
          </div>
          {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="w-10 whitespace-nowrap">Actions</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded by</th>
                  <th>Date</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {documentsQ.isLoading && (
                  <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">Loading documents…</td></tr>
                )}
                {documentsQ.data && realDocuments.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-sm text-muted-foreground">No documents on this matter yet.</td></tr>
                )}
                {realDocuments
                  .filter((d) => !docCategoryFilter || d.kind.toLowerCase() === docCategoryFilter.replace('_', ' '))
                  .map((d) => (
                  <tr
                    key={d.id}
                    className="cursor-pointer hover:bg-muted/30"
                    onClick={() => setPreviewDocId(d.id)}
                  >
                    <td>
                      {d.icon === 'pdf'
                        ? <FilePdf className="h-5 w-5 text-red-600 dark:text-red-400" weight="fill" />
                        : <FileDoc className="h-5 w-5 text-blue-600 dark:text-blue-400" weight="fill" />}
                    </td>
                    <td className="font-medium text-foreground">{d.name}</td>
                    <td><Badge>{d.kind}</Badge></td>
                    <td className="text-xs text-muted-foreground">{d.size}</td>
                    <td>{d.by || '—'}</td>
                    <td className="text-xs text-muted-foreground">{d.uploaded}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-0.5">
                        {/* Preview */}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setPreviewDocId(d.id); }}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-brand-50 hover:text-brand-600 dark:hover:text-brand-400"
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {/* Download */}
                        {d.downloadUrl && (
                          <a
                            href={d.downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                            title="Download"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                        {/* Delete — two-step confirm. Withheld from pupils: `document.delete` is not
                            granted to them, so the control only ever 403'd. */}
                        {!canDeleteDocument(user?.memberType) ? null : confirmDeleteDocId === d.id ? (
                          <span className="flex items-center gap-1 pl-1">
                            <button
                              type="button"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await deleteDocument(d.id);
                                  toast.success(`${d.name} deleted`);
                                  documentsQ.refetch();
                                } catch {
                                  toast.error('Could not delete — please try again');
                                } finally {
                                  setConfirmDeleteDocId(null);
                                }
                              }}
                              className="text-xs font-semibold text-red-600 dark:text-red-400 hover:underline"
                            >
                              Delete?
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteDocId(null); }}
                              className="text-xs text-muted-foreground hover:underline"
                            >
                              Cancel
                            </button>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteDocId(d.id); }}
                            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                            title="Delete"
                          >
                            <Trash className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Document preview modal */}
      <DocumentPreviewModal
        documentId={previewDocId}
        onClose={() => setPreviewDocId(null)}
        onDeleted={() => { setPreviewDocId(null); documentsQ.refetch(); }}
      />

      {/* Upload document modal — pre-wired to this matter */}
      <UploadDocumentModal
        open={uploadOpen}
        defaultCaseId={id}
        onClose={() => setUploadOpen(false)}
        onUpload={() => {
          setUploadOpen(false);
          queryClient.invalidateQueries({ queryKey: ['matter-documents', id] });
        }}
      />

      {/* NOTES */}
      {tab === 'notes' && (
        <div className="space-y-3">
          {!canWriteNotes && (
            <Card className="p-4 text-sm text-muted-foreground">
              {user?.memberType === 'pupil'
                ? 'You can read notes on this matter, but pupils cannot add or change them.'
                : 'Notes are private to the barristers on this matter.'}
            </Card>
          )}
          {canWriteNotes && (
          <Card className="p-3">
            <ExpandableTextarea
              label="Matter note"
              rows={4}
              value={noteText}
              onChange={setNoteText}
              placeholder="Add a matter note…"
            />
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Notes are visible to the matter team only</div>
              <Button
                variant="primary"
                size="sm"
                disabled={!noteText.trim()}
                onClick={async () => {
                  const body = noteText.trim();
                  if (!body) return;
                  try {
                    await createNote({ entityType: 'case', entityId: matter.id, caseId: matter.id, body });
                    setNoteText('');
                    await queryClient.invalidateQueries({ queryKey: ['matter-notes', id] });
                    toast.success('Note posted');
                  } catch (err) {
                    toast.error((err as Error)?.message ?? 'Could not post note');
                  }
                }}
              >
                Post note
              </Button>
            </div>
          </Card>
          )}

          {notesQ.isLoading && (
            <Card className="p-4 text-center text-sm text-muted-foreground">Loading notes…</Card>
          )}
          {notesQ.data && realNotes.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground">No notes on this matter yet.</Card>
          )}
          {realNotes.map((n) => {
            const author = authorName(n);
            // PATCH/DELETE /notes/:id existed with no controls. Only the author is offered them —
            // the same rule the server enforces (anyone else gets a 404).
            const isOwnNote = canWriteNotes && !!n.author && n.author.id === user?.id;
            const isEditing = editingNoteId === n.id;
            const busy = noteBusyId === n.id;
            return (
              <Card key={n.id} className="p-4" data-testid="matter-note">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700 dark:text-brand-300">
                    {author.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="text-sm font-semibold text-foreground">{author}</div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(n.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                    {isEditing ? (
                      <div className="mt-2 space-y-2">
                        <ExpandableTextarea label="Edit note" rows={4} value={editNoteText} onChange={(v) => setEditNoteText(v)} />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" disabled={busy} onClick={() => setEditingNoteId(null)}>Cancel</Button>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={busy || !editNoteText.trim() || editNoteText.trim() === n.body}
                            onClick={async () => {
                              setNoteBusyId(n.id);
                              try {
                                await updateNote(n.id, { body: editNoteText.trim() });
                                await queryClient.invalidateQueries({ queryKey: ['matter-notes', id] });
                                setEditingNoteId(null);
                                toast.success('Note updated');
                              } catch (err) {
                                toast.error(getApiErrorMessage(err, 'Could not update the note'));
                              } finally {
                                setNoteBusyId(null);
                              }
                            }}
                          >
                            {busy ? 'Saving…' : 'Save'}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{n.body}</p>
                    )}
                    <div className="mt-2 flex items-center gap-2">
                      {n.isPrivate && <Badge variant="warning">Private</Badge>}
                      {isOwnNote && !isEditing && (
                        <div className="ml-auto flex items-center gap-1">
                          <Button variant="ghost" size="xs" disabled={busy} onClick={() => { setEditingNoteId(n.id); setEditNoteText(n.body); }}>
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="xs"
                            disabled={busy}
                            onClick={async () => {
                              if (!window.confirm('Delete this note? This cannot be undone.')) return;
                              setNoteBusyId(n.id);
                              try {
                                await deleteNote(n.id);
                                await queryClient.invalidateQueries({ queryKey: ['matter-notes', id] });
                                toast.success('Note deleted');
                              } catch (err) {
                                toast.error(getApiErrorMessage(err, 'Could not delete the note'));
                              } finally {
                                setNoteBusyId(null);
                              }
                            }}
                          >
                            {busy ? 'Deleting…' : 'Delete'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ACTIVITIES — Clio §7 per-matter view */}
      {tab === 'activities' && <MatterActivitiesPanel matterId={matter.id} />}

      {/* FEES */}
      {tab === 'fees' && (
        <div className="space-y-6">

          {/* Money situation + the complete payment story — money tab leads with
              "where it stands / what's next" then the full chronological narrative. */}
          <MatterMoneySituation matterId={matter.id} canAct={canEditFinancials} onAction={onMoneyAction} />
          <div className="rounded-md border border-border bg-card p-5">
            <MatterPaymentStory matterId={matter.id} />
          </div>

          {/* A4: unbilled CTA — counts both fee records and unbilled activities */}
          {(() => {
            const unbilledFeeRecords = unbilledRecordsQ.data?.length ?? 0;
            const unbilledActivities = realActivities.filter(
              (a) => a.invoiceStatus === 'unbilled' && a.isBillable,
            ).length;
            const totalUnbilled = unbilledFeeRecords + unbilledActivities;
            if (totalUnbilled === 0) return null;
            return (
              <div className="flex items-center gap-4 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-5 py-3.5">
                <WarningCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
                <div className="flex-1 text-sm font-medium text-amber-900 dark:text-amber-200">
                  {totalUnbilled} unbilled item{totalUnbilled !== 1 ? 's' : ''} not yet on an invoice.
                </div>
                {canEditFinancials && (
                  <Button variant="primary" size="sm" onClick={() => setFeeNoteModalOpen(true)}>
                    Create fee note
                  </Button>
                )}
              </div>
            );
          })()}

          <div className="grid gap-4 sm:grid-cols-4">
            {[
              { label: 'Fee estimate', value: matter.feeEstimate, icon: Wallet },
              { label: 'Recorded',     value: recordedTotal,      icon: Clock },
              { label: 'Billed',       value: billedTotal,        icon: FileText },
              { label: 'Outstanding',  value: outstandingTotal,   icon: WarningCircle },
            ].map((s) => (
              <div key={s.label} className="rounded-md border border-border bg-card p-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{s.label}</div>
                  <s.icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="tabular text-2xl font-semibold text-foreground">£{s.value.toLocaleString()}</div>
              </div>
            ))}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Fee records</CardTitle>
                {canEditFinancials && (
                  <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setFeeModalOpen(true)}>
                    Add fee record
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Kind</th>
                      <th>Description</th>
                      <th>Barrister</th>
                      <th>Hours</th>
                      <th>Amount</th>
                      <th>Status</th>
                      {canEditFinancials && <th className="w-10 whitespace-nowrap">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {feeRecordsQ.isLoading && (
                      <tr><td colSpan={canEditFinancials ? 8 : 7} className="py-6 text-center text-sm text-muted-foreground">Loading fee records…</td></tr>
                    )}
                    {feeRecordsQ.data && realFeeRecords.length === 0 && (
                      <tr><td colSpan={canEditFinancials ? 8 : 7} className="py-8 text-center text-sm text-muted-foreground">No fee records on this matter yet.</td></tr>
                    )}
                    {realFeeRecords.map((f) => (
                      <tr key={f.id}>
                        <td className="text-xs text-muted-foreground">{f.date}</td>
                        <td><Badge>{f.kind}</Badge></td>
                        <td className="max-w-[200px] truncate text-sm" title={f.description}>{f.description}</td>
                        <td>{f.barrister || '—'}</td>
                        <td className="text-xs text-muted-foreground">{f.hours}</td>
                        <td className="tabular font-medium text-foreground">£{f.amount.toLocaleString()}</td>
                        <td><Badge variant={f.status === 'Billed' ? 'success' : f.status === 'Billable' ? 'info' : 'default'}>{f.status}</Badge></td>
                        {canEditFinancials && (
                          <td className="whitespace-nowrap">
                              <button
                                title="Edit fee record"
                                aria-label="Edit fee record"
                                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                                onClick={() => { setEditingFeeRecord(f); setFeeModalOpen(true); }}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </button>
                            {f.status !== 'Billed' ? (
                              <button
                                title="Delete fee record"
                                className="rounded p-1 text-muted-foreground hover:bg-danger-bg hover:text-danger"
                                onClick={async () => {
                                  if (!confirm(`Delete this ${f.kind} fee record of £${f.amount.toLocaleString()}?`)) return;
                                  try {
                                    await deleteFeeRecord(f.id);
                                    queryClient.invalidateQueries({ queryKey: ['matter-fee-records', id] });
                                    queryClient.invalidateQueries({ queryKey: ['matter-unbilled', id] });
                                    queryClient.invalidateQueries({ queryKey: ['matter-financial-summary', id] });
                                    toast.success('Fee record deleted');
                                  } catch {
                                    toast.error('Could not delete fee record');
                                  }
                                }}
                              >
                                <Trash className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <span title="Cannot delete — already invoiced" className="cursor-not-allowed rounded p-1 text-muted-foreground/30">
                                <Trash className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Activities — time + expense entries from the Activities panel */}
          {(realActivities.length > 0 || feesActivitiesQ.isLoading) && (
            <Card>
              <CardHeader>
                <CardTitle>Time &amp; Expenses</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Description</th>
                        <th>User</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {feesActivitiesQ.isLoading && (
                        <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">Loading activities…</td></tr>
                      )}
                      {realActivities.map((a) => {
                        const dur = a.durationMinutes
                          ? (() => { const h = Math.floor(a.durationMinutes / 60); const m = a.durationMinutes % 60; return h > 0 ? `${h}h ${m}m` : `${m}m`; })()
                          : (a.quantity ? `${a.quantity} units` : '—');
                        const statusCls = {
                          unbilled:     'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30',
                          draft:        'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
                          billed:       'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
                          non_billable: 'bg-zinc-100 text-zinc-600 dark:text-zinc-400 border-zinc-200',
                        }[a.invoiceStatus];
                        const statusLbl = { unbilled: 'Unbilled', draft: 'Draft', billed: 'Billed', non_billable: 'Non-billable' }[a.invoiceStatus];
                        return (
                          <tr key={a.id}>
                            <td className="text-xs text-muted-foreground">
                              {new Date(a.activityDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </td>
                            <td><Badge variant="outline">{a.entryType === 'time' ? 'Time' : 'Expense'}</Badge></td>
                            <td>
                              <div className="font-medium">{a.description}</div>
                              {a.category && <div className="text-xs text-muted-foreground">{a.category}</div>}
                            </td>
                            <td className="text-xs">{a.firmUser.firstName} {a.firmUser.lastName}</td>
                            <td className="text-xs text-muted-foreground">{dur}</td>
                            <td className="tabular font-medium text-foreground">
                              £{Number(a.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td>
                              <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${statusCls}`}>
                                {statusLbl}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Fee notes</CardTitle>
                {canEditFinancials && (
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<Plus className="h-3.5 w-3.5" />}
                    onClick={() => setFeeNoteModalOpen(true)}
                  >
                    New fee note
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Number</th>
                      <th>Issued</th>
                      <th>Due</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th className="w-10 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeNotesQ.isLoading && (
                      <tr><td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">Loading fee notes…</td></tr>
                    )}
                    {feeNotesQ.data && realFeeNotes.length === 0 && (
                      <tr><td colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No fee notes raised on this matter yet.</td></tr>
                    )}
                    {realFeeNotes.map((n) => {
                      const total    = Number(n.total);
                      const paid     = Number(n.amountPaid);
                      // MONEY-1: render the canonical server-computed outstanding (refund/credit/
                      // write-off aware, from feeNoteMoneyFacts) — never recompute a balance in the UI.
                      const balance  = Number((n as any).outstanding ?? 0);
                      return (
                      <tr key={n.id}>
                        <td className="text-xs text-brand-700 dark:text-brand-300">{n.feeNoteNumber}</td>
                        <td className="text-xs text-muted-foreground">
                          {new Date(n.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </td>
                        <td className="text-xs text-muted-foreground">
                          {new Date(n.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </td>
                        <td className="tabular font-semibold text-foreground">
                          £{total.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="tabular text-emerald-700 dark:text-emerald-300">
                          {paid > 0 ? `£${paid.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}
                        </td>
                        <td className={`tabular font-medium ${balance > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}`}>
                          {balance > 0 ? `£${balance.toLocaleString('en-GB', { maximumFractionDigits: 2 })}` : '—'}
                        </td>
                        <td><Badge variant={feeNoteStatusVariant(n.status)}>{FEE_NOTE_STATUS_LABEL[n.status] ?? n.status}</Badge></td>
                        <td>
                          <FeeNoteActionsButton
                            feeNote={n}
                            onChanged={() => {
                              queryClient.invalidateQueries({ queryKey: ['matter-fee-notes', id] });
                              queryClient.invalidateQueries({ queryKey: ['matter-financial-summary', id] });
                              queryClient.invalidateQueries({ queryKey: ['matter-financial-state', id] });
                              queryClient.invalidateQueries({ queryKey: ['matter-payment-story', id] });
                            }}
                          />
                        </td>
                        <td>
                          <PrintFeeNoteButton feeNoteId={n.id} feeNoteNumber={n.feeNoteNumber} />
                        </td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Payment history */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Payment history</CardTitle>
                {canEditFinancials && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPaymentModalOpen(true)}
                  >
                    Record payment
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Payer</th>
                      <th>Method</th>
                      <th>Ref</th>
                      <th>Allocated to</th>
                      <th>Amount</th>
                      <th>Recorded by</th>
                      <th>Status</th>
                      {canVoidPayment && <th className="w-10 whitespace-nowrap">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsQ.isLoading && (
                      <tr><td colSpan={canVoidPayment ? 9 : 8} className="py-6 text-center text-sm text-muted-foreground">Loading payments…</td></tr>
                    )}
                    {/* A REFUSAL IS NOT AN EMPTY LIST. `GET /payments` is scoped to
                        payments.routes.ts READ_ROLES, which excludes pupil and door_tenant, so for
                        them this query 403s. The row below used to be reached only on `.data`, which
                        left the table silently blank — and on a matter that HAS been paid, blank
                        reads as "nothing has been paid". Name the refusal instead. Nothing here
                        grants access; it reports the answer the server already gave. */}
                    {paymentsRefused && (
                      <tr><td colSpan={canVoidPayment ? 9 : 8} className="py-8 text-center text-sm text-muted-foreground">
                        Payment records aren't available to your role. The fee notes above show what
                        has been billed on this matter.
                      </td></tr>
                    )}
                    {!paymentsRefused && paymentsQ.data && (paymentsQ.data.payments ?? []).length === 0 && (
                      <tr><td colSpan={canVoidPayment ? 9 : 8} className="py-8 text-center text-sm text-muted-foreground">No payments recorded on this matter yet.</td></tr>
                    )}
                    {(paymentsQ.data?.payments ?? []).map((p) => {
                      const METHOD_LABEL: Record<string, string> = {
                        bank_transfer: 'Bank transfer', cheque: 'Cheque', card: 'Card',
                        cash: 'Cash', online: 'Online', other: 'Other',
                      };
                      const feeNoteNums = p.allocations.map((a: any) => a.feeNote.feeNoteNumber).join(', ');
                      // A write-off is a payment whose allocations are all write-offs; voiding it is
                      // how a write-off is reversed (the debt is reinstated). The server requires
                      // write-off authority for this, so the label names the real action.
                      const isWriteOffPayment = p.allocations.length > 0 && p.allocations.every((a: any) => a.isWriteOff);
                      const recorder = p.recordedBy
                        ? `${p.recordedBy.firstName ?? ''} ${p.recordedBy.lastName ?? ''}`.trim()
                        : '—';
                      return (
                        <tr key={p.id} className={p.voidedAt ? 'opacity-50' : ''}>
                          <td className="text-xs text-muted-foreground">
                            {new Date(p.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="text-sm">
                            {p.paidByContact
                              ? `${p.paidByContact.firstName ?? ''} ${p.paidByContact.lastName ?? ''}`.trim() || p.paidByContact.company || '—'
                              : p.payerName || '—'}
                          </td>
                          <td><Badge>{METHOD_LABEL[p.paymentMethod] ?? p.paymentMethod}</Badge></td>
                          <td className="text-xs text-muted-foreground">{p.referenceNumber || '—'}</td>
                          <td className="text-xs text-brand-700 dark:text-brand-300">{feeNoteNums || '—'}</td>
                          <td className="tabular font-semibold text-emerald-700 dark:text-emerald-300">
                            £{Number(p.totalAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="text-xs text-muted-foreground">{recorder}</td>
                          <td>
                            {p.voidedAt
                              ? <Badge variant="danger">Voided</Badge>
                              : <Badge variant="success">Received</Badge>}
                          </td>
                          {/* Void is offered ONLY on a live payment. An already-voided one has
                              nothing left to reverse, and the server answers 422 — so the control
                              is absent rather than disabled-and-lying. */}
                          {canVoidPayment && (
                            <td>
                              {!p.voidedAt && (
                                <RowActions
                                  onDelete={() => setVoidPaymentTarget({
                                    id: p.id,
                                    amount: Number(p.totalAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
                                    isWriteOff: isWriteOffPayment,
                                  })}
                                  deleteLabel={isWriteOffPayment ? 'Reverse write-off' : 'Void payment'}
                                />
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Credit notes */}
          {((creditNotesQ.data?.length ?? 0) > 0 || creditNotesQ.isLoading) && (
            <Card>
              <CardHeader>
                <CardTitle>Credit notes</CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0">
                {/* A table is the one thing that cannot reflow: on a phone these columns ran off the right edge and the last ones were simply unreachable. Scroll it sideways instead. */}
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Number</th>
                        <th>Issued</th>
                        <th>Against</th>
                        <th>Reason</th>
                        <th>Amount</th>
                        <th>Status</th>
                        {/* Voiding a credit note is an admin financial-control action (VOID_ROLES = admin). */}
                        {canVoidCreditNote && <th className="w-10 whitespace-nowrap">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {creditNotesQ.isLoading && (
                        <tr><td colSpan={canVoidCreditNote ? 7 : 6} className="py-6 text-center text-sm text-muted-foreground">Loading credit notes…</td></tr>
                      )}
                      {(creditNotesQ.data ?? []).map((cn) => (
                        <tr key={cn.id}>
                          <td className="text-xs text-brand-700 dark:text-brand-300">{cn.creditNoteNumber}</td>
                          <td className="text-xs text-muted-foreground">
                            {new Date(cn.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>
                          <td className="text-xs text-muted-foreground">
                            {cn.originalFeeNote?.feeNoteNumber ?? '—'}
                          </td>
                          <td className="max-w-xs truncate text-sm">{cn.reason}</td>
                          <td className="tabular font-semibold text-foreground">
                            £{Number(cn.totalAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td>
                            <Badge
                              variant={
                                cn.status === 'applied' ? 'success'
                                : cn.status === 'voided' ? 'danger'
                                : 'default'
                              }
                            >
                              {CREDIT_NOTE_STATUS_LABEL[cn.status] ?? cn.status}
                            </Badge>
                          </td>
                          {/* Only an APPLIED credit note is voidable — the server refuses any
                              other status ("Only applied credit notes can be voided",
                              credit-notes.service.ts:408-410), so a draft or already-voided note
                              gets no control at all. */}
                          {canVoidCreditNote && (
                            <td>
                              {cn.status === 'applied' && (
                                <RowActions
                                  onDelete={() => setVoidCreditNoteTarget({ id: cn.id, number: cn.creditNoteNumber })}
                                  deleteLabel="Void credit note"
                                />
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Communications on this matter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="py-4 text-center text-sm text-muted-foreground">
                In-app threads live in the{' '}
                <button
                  className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                  onClick={() => setTab('messages')}
                >
                  Messages
                </button>{' '}
                tab. Inbound / outbound email and phone-call logging is on the roadmap.
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TASKS */}
      {tab === 'tasks' && (() => {
        const isSolo = chambersMode.isSolo;
        const activeTasks = realTasks
          .filter((t) => ACTIVE_STATUSES.includes(t.status))
          .sort((a, b) => {
            const aOver = a.dueDate && new Date(a.dueDate) < new Date() ? -100 : 0;
            const bOver = b.dueDate && new Date(b.dueDate) < new Date() ? -100 : 0;
            if (aOver !== bOver) return aOver - bOver;
            if (a.dueDate && b.dueDate) return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
            return 0;
          });
        const completedTasks = realTasks.filter((t) => t.status === 'completed');

        function statusBorderCls(s: TaskStatus) {
          switch (s) {
            case 'blocked': return 'border-l-[3px] border-l-red-400';
            case 'in_progress': return 'border-l-[3px] border-l-brand-500';
            case 'awaiting_barrister': case 'awaiting_clerk': return 'border-l-[3px] border-l-amber-400';
            case 'hearing_followup': case 'filing_pending': return 'border-l-[3px] border-l-purple-400';
            default: return 'border-l-[3px] border-l-transparent';
          }
        }

        function priorityBadgeCls(p: TaskPriority) {
          const map: Record<TaskPriority, string> = {
            urgent: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30',
            high:   'bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
            normal: 'hidden',
            low:    'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-500/30',
          };
          return map[p];
        }

        function statusBadgeCls(s: TaskStatus) {
          if (s === 'pending') return 'hidden';
          const map: Record<TaskStatus, string> = {
            completed:          'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30',
            in_progress:        'bg-brand-50 text-brand-700 dark:text-brand-300 border border-brand-200',
            blocked:            'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-500/30',
            cancelled:          'bg-zinc-100 text-zinc-500 border border-zinc-200',
            awaiting_barrister: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
            awaiting_clerk:     'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30',
            hearing_followup:   'bg-purple-50 text-purple-700 dark:text-purple-300 border border-purple-200',
            filing_pending:     'bg-purple-50 text-purple-700 dark:text-purple-300 border border-purple-200',
            pending:            'hidden',
          };
          return map[s];
        }

        return (
          <Card>
            <div className="flex items-center justify-between border-b border-border px-6 py-3">
              <div className="text-sm text-muted-foreground">
                {activeTasks.length} active · {completedTasks.length} completed
              </div>
              {canEditFinancials && (
                <Button variant="primary" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} onClick={() => setTaskModalOpen(true)}>
                  Add task
                </Button>
              )}
            </div>
            <div className="divide-y divide-border">
              {tasksQ.isLoading && (
                <div className="px-6 py-8 text-center text-sm text-muted-foreground">Loading tasks…</div>
              )}
              {tasksQ.data && realTasks.length === 0 && (
                <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                  <p className="text-sm font-medium text-foreground">No tasks on this matter yet</p>
                  <p className="text-xs text-muted-foreground">Add a task to track preparation steps, deadlines, or follow-ups.</p>
                  <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />} className="mt-2" onClick={() => setTaskModalOpen(true)}>
                    Add first task
                  </Button>
                </div>
              )}

              {/* Active task rows — click to open detail panel */}
              {activeTasks.map((t) => {
                const isOverdue = t.dueDate && new Date(t.dueDate) < new Date();
                return (
                  <div
                    key={t.id}
                    className={`group cursor-pointer transition hover:bg-muted/20 ${statusBorderCls(t.status)}`}
                    onClick={() => setDetailTask(t)}
                  >
                    <div className="flex items-start gap-3 px-6 py-3.5">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="text-sm font-medium text-foreground">{t.title}</span>
                          {t.priority !== 'normal' && (
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${priorityBadgeCls(t.priority as TaskPriority)}`}>
                              {t.priority}
                            </span>
                          )}
                          {t.status !== 'pending' && (
                            <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusBadgeCls(t.status)}`}>
                              {TASK_STATUS_LABELS[t.status]}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0 text-xs text-muted-foreground/70">
                          {!isSolo && t.assignedTo && (
                            <span>{t.assignedTo.firstName} {t.assignedTo.lastName}</span>
                          )}
                          {t.hearing && (
                            <span>{t.hearing.hearingType} · {new Date(t.hearing.hearingDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                          )}
                          {t.dueDate ? (
                            <span className={isOverdue ? 'font-medium text-red-600 dark:text-red-400' : ''}>
                              {isOverdue ? 'Overdue — ' : 'Due '}
                              {new Date(t.dueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40">No due date</span>
                          )}
                        </div>
                      </div>
                      {/* View / Edit / Delete on the row itself — the same shared control every
                          other list uses. These actions previously existed only inside the
                          detail panel, so the row looked read-only. */}
                      <div onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          onView={() => setDetailTask(t)}
                          onEdit={canEditFinancials ? () => setEditTaskTarget(t) : undefined}
                          onDelete={canEditFinancials ? () => deleteTaskMut.mutate(t.id) : undefined}
                          deleteConfirm={{
                            title: 'Delete task?',
                            message: `Delete “${t.title}”? This removes it from the matter.`,
                            confirmLabel: 'Delete',
                          }}
                        />
                      </div>
                      <ArrowSquareOut size={13} className="mt-0.5 shrink-0 text-muted-foreground/20 transition group-hover:text-muted-foreground/60" />
                    </div>
                  </div>
                );
              })}

              {/* Completed record rows */}
              {completedTasks.length > 0 && (
                <>
                  <div className="px-6 py-2 bg-muted/20">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/50">
                      Operational history · {completedTasks.length}
                    </span>
                  </div>
                  {completedTasks.map((t) => {
                    const completedTs = t.completedAt ?? t.updatedAt;
                    return (
                      <div
                        key={t.id}
                        className="group cursor-pointer border-l-[3px] border-l-emerald-400 transition hover:bg-muted/10"
                        onClick={() => setDetailTask(t)}
                      >
                        <div className="flex items-start gap-3 px-6 py-3.5">
                          <CheckCircle weight="fill" size={15} className="mt-0.5 shrink-0 text-emerald-500" />
                          <div className="flex-1 min-w-0">
                            <span className="text-sm text-foreground/70">{t.title}</span>
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground/60">
                              <span>
                                Completed {new Date(completedTs).toLocaleString('en-GB', {
                                  day: '2-digit', month: 'short', year: 'numeric',
                                  hour: '2-digit', minute: '2-digit',
                                })}
                              </span>
                              {!isSolo && t.completedBy && (
                                <>
                                  <span className="text-muted-foreground/30">·</span>
                                  <span>{t.completedBy.firstName} {t.completedBy.lastName}</span>
                                </>
                              )}
                            </div>
                            {t.completionNote && (
                              <div className="mt-1.5 rounded border-l-2 border-emerald-200 dark:border-emerald-500/30 pl-2.5 text-xs text-muted-foreground/70 italic">
                                {t.completionNote}
                              </div>
                            )}
                          </div>
                          <ArrowSquareOut size={13} className="mt-0.5 shrink-0 text-muted-foreground/20 transition group-hover:text-muted-foreground/60" />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </Card>
        );
      })()}

      {taskModalOpen && (
        <TaskModal
          matterId={matter.id}
          defaultAssigneeId={user?.id}
          onClose={() => setTaskModalOpen(false)}
          onSaved={() => {
            setTaskModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['matter-tasks', id] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}
      {editTaskTarget && (
        <TaskModal
          matterId={matter.id}
          initial={editTaskTarget}
          onClose={() => setEditTaskTarget(null)}
          onSaved={() => {
            setEditTaskTarget(null);
            queryClient.invalidateQueries({ queryKey: ['matter-tasks', id] });
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {/* Task detail panel — slides in from the right */}
      {detailTask && (
        <TaskDetailPanel
          task={detailTask}
          isSolo={chambersMode.isSolo}
          canEdit={
            detailTask.assignedToId === user?.id ||
            detailTask.createdById === user?.id ||
            user?.memberType === 'admin' ||
            user?.memberType === 'senior_clerk'
          }
          onClose={() => setDetailTask(null)}
          onEdit={() => { setEditTaskTarget(detailTask); setDetailTask(null); }}
          onComplete={() => setCompleteTarget(detailTask)}
          onReopen={() => reopenTaskMut.mutate(detailTask.id)}
          onDelete={() => {
            if (window.confirm(`Delete "${detailTask.title}"?`)) deleteTaskMut.mutate(detailTask.id);
          }}
          onNavigateToMatter={(matterId) => navigate(`${basePath}/matters/${matterId}`)}
        />
      )}

      {/* Record completion dialog */}
      {completeTarget && user && (
        <CompleteTaskDialog
          task={completeTarget}
          completingUserName={`${user.firstName} ${user.lastName}`}
          isPending={completeTaskMut.isPending}
          onClose={() => setCompleteTarget(null)}
          onConfirm={(note, file) =>
            completeTaskMut.mutate({ taskId: completeTarget.id, note, file })
          }
        />
      )}

      {tab === 'messages' && (
        <div className="space-y-4">
          {roles?.instructingSolicitorId && (
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
              <div className="text-sm text-muted-foreground">
                Instructing solicitor: <span className="font-medium text-foreground">{roles.instructingSolicitorName || 'on record'}</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<ChatTeardropDots className="h-3.5 w-3.5" />}
                onClick={() => {
                  const params = new URLSearchParams({
                    matterId: matter.id,
                    kind: 'matter_external',
                  });
                  navigate(`${basePath}/communications?new=1&${params.toString()}`);
                }}
              >
                Message solicitor
              </Button>
            </div>
          )}
          <MessagingView matterId={matter.id} defaultKind="matter_internal" height="calc(100dvh - 280px)" />
        </div>
      )}

      {/* HISTORY — full audit trail for this matter */}
      {tab === 'history' && <MatterHistoryPanel matterId={matter.id} />}

      {tab === 'email' && <MatterEmailPanel matterId={matter.id} />}

      {/* CONVERSATIONS — communication unified by professional objective (Conversation Engine) */}
      {tab === 'conversations' && <MatterConversationsPanel matterId={matter.id} />}

      {/* Edit Matter Modal */}
      {editOpen && (
        <EditMatterModal
          matter={matter}
          // Announce the save only once the server has accepted it. It used to cheer
          // immediately, so a refusal ("cannot archive a matter with active hearings
          // scheduled") landed next to a success message that was already wrong.
          onSave={async (patch) => {
            setEditOpen(false);
            if (await updateMatter(matter.id, patch)) toast.success('Matter updated successfully');
          }}
          onClose={() => setEditOpen(false)}
          onCloseMatter={() => { setEditOpen(false); setCloseMatterOpen(true); }}
        />
      )}

      {/* New fee record */}
      <NewFeeRecordModal
        caseId={matter.id}
        caseLabel={`${matter.number} ${matter.name}`}
        open={feeModalOpen}
        record={editingFeeRecord}
        onClose={() => { setFeeModalOpen(false); setEditingFeeRecord(null); }}
        onSuccess={() => {
          fetchMatters();
          queryClient.invalidateQueries({ queryKey: ['matter-fee-records', id] });
          queryClient.invalidateQueries({ queryKey: ['matter-unbilled', id] });
          queryClient.invalidateQueries({ queryKey: ['matter-financial-summary', id] });
          queryClient.invalidateQueries({ queryKey: ['matter-financial-state', id] });
          queryClient.invalidateQueries({ queryKey: ['matter-payment-story', id] });
        }}
      />

      {feeNoteModalOpen && (
        <NewFeeNoteModal
          open={feeNoteModalOpen}
          onClose={() => setFeeNoteModalOpen(false)}
          presetMatterId={matter.id}
          onSave={() => {
            setFeeNoteModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['matter-fee-notes', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-fee-records', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-unbilled', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-financial-summary', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-financial-state', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-payment-story', id] });
            fetchMatters();
          }}
        />
      )}

      {paymentModalOpen && (
        <RecordPaymentModal
          presetMatterId={matter.id}
          onClose={() => setPaymentModalOpen(false)}
          onSaved={() => {
            setPaymentModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['matter-fee-notes', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-financial-summary', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-financial-state', id] });
            queryClient.invalidateQueries({ queryKey: ['matter-payment-story', id] });
            fetchMatters();
          }}
        />
      )}

      {/* Void payment — local reason capture. Bounds mirror payments.schema.ts:88-90. */}
      <VoidReasonModal
        open={!!voidPaymentTarget}
        title={voidPaymentTarget?.isWriteOff ? 'Reverse write-off' : 'Void payment'}
        description={voidPaymentTarget?.isWriteOff
          ? `Reversing this £${voidPaymentTarget?.amount ?? ''} write-off reinstates the debt: the fee notes it cleared will show the balance as owed again. The write-off is kept on the record, marked Voided — it is not deleted.`
          : `Voiding this £${voidPaymentTarget?.amount ?? ''} payment reverses it: the fee notes it was allocated to will show the money as outstanding again. The payment is kept on the record, marked Voided — it is not deleted.`}
        reasonLabel={voidPaymentTarget?.isWriteOff ? 'Reason for reversing this write-off' : 'Reason for voiding'}
        minLength={1}
        maxLength={500}
        placeholder={voidPaymentTarget?.isWriteOff ? 'e.g. Written off in error — client has agreed to pay' : 'e.g. Cheque returned unpaid'}
        pending={voidPaymentMut.isPending}
        onCancel={() => setVoidPaymentTarget(null)}
        onConfirm={(reason) => {
          if (voidPaymentTarget) voidPaymentMut.mutate({ paymentId: voidPaymentTarget.id, reason, isWriteOff: voidPaymentTarget.isWriteOff });
        }}
      />

      {/* Void credit note — bounds mirror credit-notes.schema.ts:17-18. */}
      <VoidReasonModal
        open={!!voidCreditNoteTarget}
        title="Void credit note"
        description={`Voiding credit note ${voidCreditNoteTarget?.number ?? ''} restores the amount it credited, so the fee note it was issued against becomes outstanding again. The credit note is kept on the record, marked Voided.`}
        reasonLabel="Reason for voiding"
        minLength={5}
        maxLength={2000}
        placeholder="e.g. Issued against the wrong fee note"
        pending={voidCreditNoteMut.isPending}
        onCancel={() => setVoidCreditNoteTarget(null)}
        onConfirm={(voidReason) => {
          if (voidCreditNoteTarget) voidCreditNoteMut.mutate({ creditNoteId: voidCreditNoteTarget.id, voidReason });
        }}
      />

      {/* Add / update parties */}
      <AddPartyModal
        caseId={matter.id}
        caseLabel={`${matter.number} ${matter.name}`}
        open={partyModalOpen}
        onClose={() => setPartyModalOpen(false)}
        onSuccess={() => {
          fetchMatters();
          queryClient.invalidateQueries({ queryKey: ['matter-roles', id] });
        }}
      />

      {/* Record Outcome — inline modal, same pattern as HearingsPage for consistency.
          Belt and braces: the trigger above is already withheld from pupils, but the modal
          gates too so no other path can open a form the API will refuse. */}
      {recordOutcomeHearing && canRecordHearingOutcome(user?.memberType) && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
          <ScrollLock />
          <div className="my-auto w-full max-w-md rounded-xl border border-border bg-card shadow-2xl">
            <div className="border-b border-border px-5 py-4">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Record outcome</div>
              <div className="mt-0.5 text-base font-semibold text-foreground">
                {recordOutcomeHearing.type} · {recordOutcomeHearing.date}
              </div>
              <div className="text-sm text-muted-foreground">{matter.name}</div>
            </div>
            <div className="px-5 py-4">
              <label className="mb-1.5 block text-sm font-medium text-foreground">Outcome notes</label>
              <ExpandableTextarea
                label="Outcome notes"
                rows={4}
                placeholder="e.g. Judgment granted. Defence to file costs schedule by 28 June."
                value={outcomeText}
                onChange={setOutcomeText}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                This records the hearing as completed and adds a timeline event to the matter.
                For full details (orders, costs, deadlines) open the{' '}
                <button
                  className="font-medium text-brand-600 dark:text-brand-400 hover:underline"
                  onClick={() => { setRecordOutcomeHearing(null); navigate(`${basePath}/hearings/${recordOutcomeHearing.id}`); }}
                >
                  hearing detail page
                </button>.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
              <Button variant="outline" size="sm" onClick={() => { setRecordOutcomeHearing(null); setOutcomeText(''); }} disabled={savingOutcome}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!outcomeText.trim() || savingOutcome}
                onClick={async () => {
                  setSavingOutcome(true);
                  try {
                    await storeUpdateHearing(recordOutcomeHearing.id, { outcome: outcomeText.trim(), status: 'completed' });
                    toast.success('Outcome recorded');
                    setRecordOutcomeHearing(null);
                    setOutcomeText('');
                  } catch {
                    toast.error('Could not save outcome');
                  } finally {
                    setSavingOutcome(false);
                  }
                }}
              >
                {savingOutcome ? 'Saving…' : 'Save outcome'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Close Matter modal */}
      {closeMatterOpen && matter && (
        <CloseMatterModal
          matterId={matter.id}
          matterNumber={matter.number}
          matterName={matter.name}
          onClose={() => setCloseMatterOpen(false)}
          onClosed={() => {
            setCloseMatterOpen(false);
            queryClient.invalidateQueries({ queryKey: ['matter-by-id', id] });
            fetchMatters();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Edit Matter Modal
// ─────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [...MATTER_STATUSES];
const RISK_OPTIONS: { value: string; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function EditMatterModal({ matter, onSave, onClose, onCloseMatter }: { matter: Matter; onSave: (patch: Partial<Matter>) => void; onClose: () => void; onCloseMatter: () => void }) {
  const [form, setForm] = useState({
    name: matter.name,
    number: matter.number,
    status: matter.status,
    riskRating: matter.riskRating,
    area: matter.area,
    practiceAreaId: matter.practiceAreaId ?? '',
    subArea: matter.subArea,
    court: matter.court,
    courtId: matter.courtId ?? '',
    courtroom: matter.courtroom,
    judge: matter.judge,
    feeEstimate: matter.feeEstimate,
    summary: matter.summary,
    internalNotes: matter.internalNotes,
  });
  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => setForm((f) => ({ ...f, [field]: value }));

  const [courts, setCourts] = useState<Court[]>([]);
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);

  useEffect(() => {
    fetchCourts().then((c) => setCourts(c.filter((x) => x.isActive))).catch(() => {});
    fetchPracticeAreas({ isActive: true }).then(setPracticeAreas).catch(() => {});
  }, []);

  // Ending a matter is not an edit. The close ceremony states the consequences and records
  // WHY; a Save here did all of it silently. Same hand-over as the shared edit modal.
  const closingHere = form.status !== matter.status && isClosingMatterStatus(form.status);

  const handleSave = () => {
    if (closingHere) {
      onClose();
      onCloseMatter();
      return;
    }
    onSave({
      name: form.name,
      status: form.status,
      riskRating: form.riskRating,
      area: form.area,
      practiceAreaId: form.practiceAreaId || null,
      subArea: form.subArea,
      court: form.court,
      courtId: form.courtId || null,
      courtroom: form.courtroom,
      judge: form.judge,
      feeEstimate: form.feeEstimate,
      summary: form.summary,
      internalNotes: form.internalNotes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-16" onClick={onClose}>
      <ScrollLock />
      <Card className="w-full max-w-2xl shadow-pop" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="text-base font-semibold text-foreground">Edit Matter</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{form.number}</div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <CardContent className="max-h-[70vh] overflow-y-auto space-y-5 p-6">
          {/* Row: Name */}
          <FormField label="Matter name *" value={form.name} onChange={(v) => set('name', v)} placeholder="e.g. R v Whitfield" />

          {/* Row: Status + Risk */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)} className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm capitalize focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100">
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Priority</label>
              <select value={form.riskRating} onChange={(e) => set('riskRating', e.target.value)} className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100">
                {RISK_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>

          {/* Row: Practice area + Sub-area */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Practice area</label>
              <SearchableSelect
                value={form.practiceAreaId}
                onChange={(id) => {
                  const pa = practiceAreas.find((p) => p.id === id);
                  set('practiceAreaId', id);
                  set('area', pa?.name ?? id);
                }}
                placeholder="Select practice area…"
                options={practiceAreas.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
            <FormField label="Sub-area" value={form.subArea} onChange={(v) => set('subArea', v)} placeholder="e.g. Serious & complex fraud" />
          </div>

          {/* Row: Court + Courtroom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Court</label>
              <SearchableSelect
                value={form.courtId}
                onChange={(id) => {
                  const c = courts.find((x) => x.id === id);
                  set('courtId', id);
                  set('court', c?.shortName ?? id);
                }}
                placeholder="Select court…"
                options={courts.map((c) => ({ value: c.id, label: `${c.shortName} — ${c.name}` }))}
              />
            </div>
            <FormField label="Courtroom" value={form.courtroom} onChange={(v) => set('courtroom', v)} placeholder="e.g. Court 4, Royal Courts of Justice" />
          </div>

          {/* Row: Judge + Fee */}
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Judge" value={form.judge} onChange={(v) => set('judge', v)} placeholder="e.g. Smith J" />
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Fee estimate (&pound;)</label>
              <input
                type="number"
                value={form.feeEstimate}
                onChange={(e) => set('feeEstimate', parseFloat(e.target.value) || 0)}
                className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm tabular focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
              />
            </div>
          </div>

          {/* Summary */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Matter summary</label>
            <ExpandableTextarea
              label="Matter summary"
              rows={4}
              value={form.summary}
              onChange={(v) => set('summary', v)}
              placeholder="Summary of the case — visible on the overview tab"
            />
          </div>

          {/* Internal notes */}
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Internal notes</label>
            <ExpandableTextarea
              label="Internal notes"
              rows={4}
              value={form.internalNotes}
              onChange={(v) => set('internalNotes', v)}
              placeholder="Internal note — visible to matter team only"
            />
          </div>

          {/* Opened info (read-only) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opened</label>
              <div className="h-10 flex items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground">
                {matter.createdAt ? new Date(matter.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Opened by</label>
              <div className="h-10 flex items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-muted-foreground">
                {matter.createdBy || '—'}
              </div>
            </div>
          </div>
        </CardContent>

        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            leftIcon={<CheckCircle className="h-4 w-4" />}
            disabled={!form.name.trim()}
            onClick={handleSave}
          >
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function DetailRow({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 text-muted-foreground">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
        <div className={`mt-0.5 text-sm ${mono ? 'text-xs' : ''} text-foreground`}>{value}</div>
      </div>
    </div>
  );
}

function TimelineRow({ event, compact }: { event: TimelineEvent; compact?: boolean }) {
  const iconMap: Record<TimelineEvent['kind'], React.ReactNode> = {
    opened:        <Star className="h-4 w-4 text-amber-600 dark:text-amber-400" weight="fill" />,
    hearing:       <Gavel className="h-4 w-4 text-brand-600 dark:text-brand-400" />,
    document:      <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />,
    note:          <NoteIcon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
    fee_note:      <Wallet className="h-4 w-4 text-amber-600 dark:text-amber-400" />,
    task:          <ListChecks className="h-4 w-4 text-violet-600 dark:text-violet-400" />,
    communication: <ChatCircle className="h-4 w-4 text-muted-foreground" />,
    status:        <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />,
    meeting:       <Calendar className="h-4 w-4 text-teal-600 dark:text-teal-400" />,
  };

  if (compact) {
    return (
      <div className="flex items-start gap-3 px-6 py-3">
        <div className="mt-0.5 shrink-0">{iconMap[event.kind]}</div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-foreground">{event.title}</div>
          {event.detail && <div className="mt-0.5 truncate text-xs text-muted-foreground">{event.detail}</div>}
          <div className="mt-0.5 text-xs text-muted-foreground">
            {event.actor && <span>{event.actor} · </span>}
            <span className="">{event.when}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex items-start gap-4">
      <div className="z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-card">
        {iconMap[event.kind]}
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="text-sm font-semibold text-foreground">{event.title}</div>
          <div className="text-xs text-muted-foreground">{event.when}</div>
        </div>
        {event.detail && <p className="mt-1 text-sm text-foreground">{event.detail}</p>}
        <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
          {event.actor && <span>{event.actor}</span>}
          {event.meta && <><span>·</span><span>{event.meta}</span></>}
          {event.link && (
            <>
              <span>·</span>
              <Link to={event.link} className="font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300">
                Open <ArrowSquareOut className="ml-0.5 inline h-3 w-3" />
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Hearing Readiness Check ──────────────────────────────────

function ReadinessCheck({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`flex items-center gap-1.5 text-xs font-medium ${ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
      {ok
        ? <CheckCircle className="h-3.5 w-3.5" weight="fill" />
        : <WarningCircle className="h-3.5 w-3.5" weight="fill" />
      }
      {label}
    </div>
  );
}

// ─── Print fee note button ───────────────────────────────────

function PrintFeeNoteButton({ feeNoteId, feeNoteNumber }: { feeNoteId: string; feeNoteNumber: string }) {
  const [loading, setLoading] = useState(false);

  const handlePrint = async () => {
    setLoading(true);
    try {
      // Fetch as blob to carry the auth header, then open as object URL.
      const { api } = await import('@/lib/api');
      const res = await api.get(`/fee-notes/${feeNoteId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url; a.target = '_blank'; a.rel = 'noopener';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      const { toast } = await import('sonner');
      toast.error('Could not open invoice PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handlePrint}
      disabled={loading}
      title={"Print invoice " + feeNoteNumber}
      className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
    >
      {loading
        ? <span className="h-3.5 w-3.5 animate-spin rounded-full border border-current border-t-transparent" />
        : <Printer size={14} />
      }
    </button>
  );
}

// ─── Email History Panel ───────────────────────────────────────────────────────

function statusIcon(status: EmailStatus) {
  switch (status) {
    case 'delivered': return <CheckCircleIcon weight="fill" className="h-4 w-4 text-emerald-500" />;
    case 'bounced':   return <WarningIcon weight="fill" className="h-4 w-4 text-red-500" />;
    case 'complained': return <WarningIcon weight="fill" className="h-4 w-4 text-amber-500" />;
    case 'sent':      return <CheckCircleIcon className="h-4 w-4 text-brand-400" />;
    case 'received':  return <ArrowBendUpLeft weight="bold" className="h-4 w-4 text-brand-600 dark:text-brand-400" />;
    case 'queued':
    case 'failed':
    default:          return <Spinner className="h-4 w-4 text-muted-foreground" />;
  }
}

function statusLabel(status: EmailStatus): string {
  switch (status) {
    case 'delivered':  return 'Delivered';
    case 'bounced':    return 'Bounced';
    case 'complained': return 'Spam complaint';
    case 'sent':       return 'Sent';
    case 'received':   return 'Reply received';
    case 'queued':     return 'Queued';
    case 'failed':     return 'Failed';
    default:           return status;
  }
}

function redactEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at < 0) return email;
  const local = email.slice(0, at);
  const domain = email.slice(at);
  return local.slice(0, 2) + '***' + domain;
}

function senderName(addr: string): string {
  const m = addr.match(/^"?([^"<]+?)"?\s*<[^>]+>$/);
  if (m && m[1]) return m[1].trim();
  const local = (addr.split('@')[0] ?? addr).replace(/[._-]+/g, ' ').trim();
  return local.replace(/\b\w/g, (c) => c.toUpperCase()) || addr;
}

function LinkedEmailList({ emails }: { emails: MatterSyncedEmail[] }) {
  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 border-b border-border/60 bg-muted/20 px-4 py-2.5">
        <EnvelopeSimple className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-medium text-foreground flex-1">Linked emails</span>
        <span className="text-xs text-muted-foreground shrink-0">
          {emails.length} email{emails.length !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="divide-y divide-border/40">
        {emails.map((e) => {
          const isOutbound = e.direction === 'outbound';
          const counterparty = isOutbound ? (e.toAddresses[0] ?? '') : e.fromAddress;
          const when = e.receivedAt ?? e.sentAt;
          return (
            <div key={e.id} className="flex items-start gap-3 px-4 py-3">
              <div className="mt-0.5 shrink-0">
                {isOutbound
                  ? <ArrowBendUpLeft weight="bold" className="h-4 w-4 -scale-x-100 text-brand-500" />
                  : <ArrowBendUpLeft weight="bold" className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm truncate ${e.isRead ? 'font-medium text-foreground' : 'font-semibold text-foreground'}`}>
                    {e.subject || '(no subject)'}
                  </span>
                  {!e.isRead && !isOutbound && (
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />
                  )}
                  {e.attachmentCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Paperclip className="h-3 w-3" />{e.attachmentCount}
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {isOutbound ? 'To ' : 'From '}{senderName(counterparty)}
                  {when ? ` · ${new Date(when).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                </div>
                {e.snippet && (
                  <div className="mt-1 text-xs text-muted-foreground/80 line-clamp-2">{e.snippet}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MatterEmailPanel({ matterId }: { matterId: string }) {
  const { data: linked, isLoading: linkedLoading } = useQuery<MatterSyncedEmail[]>({
    queryKey: ['matter-synced-emails', matterId],
    queryFn: () => fetchMatterSyncedEmails(matterId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const { data: threads, isLoading: threadsLoading } = useQuery<EmailThread[]>({
    queryKey: ['email-threads', matterId],
    queryFn: () => fetchEmailThreadsForMatter(matterId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  if (linkedLoading || threadsLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        <span className="h-4 w-4 animate-spin rounded-full border border-current border-t-transparent mr-2" />
        Loading email history
      </div>
    );
  }

  const hasLinked = linked && linked.length > 0;
  const hasThreads = threads && threads.length > 0;

  if (!hasLinked && !hasThreads) {
    return (
      <div className="py-16 text-center text-sm text-muted-foreground">
        <EnvelopeSimple className="mx-auto mb-3 h-8 w-8 opacity-30" />
        No emails linked, sent or received on this matter.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasLinked && <LinkedEmailList emails={linked} />}
      {hasThreads && (
        <div className="space-y-1">
          {hasLinked && (
            <div className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Delivery log
            </div>
          )}
          <div className="space-y-3">
      {threads!.map((thread) => (
        <div key={thread.id} className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="flex items-center gap-3 border-b border-border/60 bg-muted/20 px-4 py-2.5">
            <EnvelopeSimple className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="text-sm font-medium text-foreground truncate flex-1">{thread.subject}</span>
            <span className="text-xs text-muted-foreground shrink-0">
              {thread.messages.length} message{thread.messages.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div className="divide-y divide-border/40">
            {thread.messages.map((msg) => {
              const isOutbound = msg.direction === 'outbound';
              const primaryAddress = isOutbound
                ? (msg.toAddresses[0] ?? '')
                : msg.fromAddress;
              const statusAt = msg.deliveredAt ?? msg.sentAt;

              return (
                <div key={msg.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="mt-0.5 shrink-0">{statusIcon(msg.status)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-foreground">
                        {isOutbound ? 'Sent' : 'Reply received'}
                      </span>
                      <span className="text-sm text-muted-foreground truncate">
                        {isOutbound ? '→' : '←'} {redactEmail(primaryAddress)}
                      </span>
                      {msg.ccAddresses.length > 0 && (
                        <span className="text-xs text-muted-foreground">
                          +{msg.ccAddresses.length} CC
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground">
                        {statusLabel(msg.status)}
                        {statusAt ? ` · ${new Date(statusAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
                      </span>
                      {msg.template && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                          {msg.template === 'document_send' ? 'Document' :
                           msg.template === 'fee_note' ? 'Fee note' :
                           msg.template === 'external' ? 'External' : msg.template}
                        </span>
                      )}
                      {msg.status === 'bounced' && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/20 dark:text-red-400">
                          Delivery failed — resend required
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
          </div>
        </div>
      )}
    </div>
  );
}
